// Effets cosmétiques (cosmétiques refaits) : auras, traînées, effets de tir, d'élimination et de mort.
// Particules en lot (ParticleContainer, une seule texture partagée) : des centaines d'étincelles pour le prix
// d'un sprite. Tout passe par `budget` (réglage « particules », animations réduites) et un plafond global ;
// les effets utiles au jeu (impacts, explosions annoncées) restent dans particles.ts et ne sont jamais rognés ici.
import { Container, Graphics, Particle, ParticleContainer, Rectangle, Texture, type Renderer } from 'pixi.js';

type Tex =
  | 'dot'
  | 'spark'
  | 'flake'
  | 'smoke'
  | 'star'
  | 'bubble'
  | 'shard'
  | 'ring'
  | 'rune'
  | 'leaf'
  | 'petal'
  | 'feather'
  | 'coin'
  | 'bat'
  | 'butterfly'
  | 'pixel'
  | 'flame'
  | 'lantern'
  | 'ghost'
  | 'nut'
  | 'screw'
  | 'jelly'
  | 'planet'
  | 'orb'
  | 'beam'
  | 'crystal'
  | 'flower'
  | 'splat'
  // Noël (saison 2)
  | 'bulb'
  | 'bauble'
  | 'cap'
  | 'nstar'
  | 'bell'
  | 'gift'
  | 'giftrib'
  | 'lid'
  | 'bow'
  | 'strip'
  | 'chunk'
  | 'snowball'
  | 'snowman';
const TEXS: Tex[] = [
  'dot', 'spark', 'flake', 'smoke', 'star', 'bubble', 'shard', 'ring', 'rune', 'leaf', 'petal', 'feather', 'coin', 'bat', 'butterfly', 'pixel',
  'flame', 'lantern', 'ghost', 'nut', 'screw', 'jelly', 'planet', 'orb', 'beam', 'crystal', 'flower', 'splat', 'bulb', 'bauble', 'cap', 'nstar',
  'bell', 'gift', 'giftrib', 'lid', 'bow', 'strip', 'chunk', 'snowball', 'snowman',
];
const CELL = 48;
/** Cases par rangée de l'atlas (16 × 48 px × résolution 2 = 1536 px de large : sans risque sur téléphone).
 *  41 textures : 3 rangées (1536 × 288 px), place pour 7 de plus avant une quatrième rangée. */
const COLS = 16;

/** Atlas des textures de particules, dessinées une fois en blanc (teintées à l'emploi ; les gris deviennent des ombres). */
function buildAtlas(renderer: Renderer): Record<Tex, Texture> {
  const g = new Graphics();
  const W = 0xffffff;
  TEXS.forEach((t, i) => {
    const cx = (i % COLS) * CELL + CELL / 2;
    const cy = Math.floor(i / COLS) * CELL + CELL / 2;
    switch (t) {
      case 'dot':
        for (let k = 8; k >= 1; k--) g.circle(cx, cy, k * 2.6).fill({ color: W, alpha: 0.11 + (8 - k) * 0.02 });
        g.circle(cx, cy, 3).fill(W);
        break;
      case 'spark':
        g.ellipse(cx, cy, 20, 2.2).fill({ color: W, alpha: 0.5 });
        g.ellipse(cx, cy, 12, 1.2).fill(W);
        break;
      case 'flake':
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          g.moveTo(cx, cy).lineTo(cx + Math.cos(a) * 16, cy + Math.sin(a) * 16).stroke({ width: 2.2, color: W });
          const mx = cx + Math.cos(a) * 9;
          const my = cy + Math.sin(a) * 9;
          for (const d of [-0.7, 0.7]) g.moveTo(mx, my).lineTo(mx + Math.cos(a + d) * 5, my + Math.sin(a + d) * 5).stroke({ width: 1.6, color: W });
        }
        break;
      case 'smoke':
        for (let k = 10; k >= 1; k--) g.circle(cx, cy, k * 2.2).fill({ color: W, alpha: 0.07 });
        break;
      case 'star': {
        const pts: number[] = [];
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          const r = k % 2 ? 4 : 18;
          pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
        }
        g.poly(pts).fill(W);
        g.circle(cx, cy, 8).fill({ color: W, alpha: 0.35 });
        break;
      }
      case 'bubble':
        g.circle(cx, cy, 14).stroke({ width: 2, color: W, alpha: 0.9 });
        g.circle(cx - 5, cy - 5, 3).fill({ color: W, alpha: 0.8 });
        break;
      case 'shard':
        g.poly([cx - 10, cy - 7, cx + 12, cy - 2, cx - 4, cy + 9]).fill(W);
        break;
      case 'ring':
        g.circle(cx, cy, 20).stroke({ width: 3, color: W });
        g.circle(cx, cy, 20).stroke({ width: 7, color: W, alpha: 0.25 });
        break;
      case 'rune':
        g.moveTo(cx - 8, cy + 12).lineTo(cx, cy - 12).lineTo(cx + 8, cy + 12).moveTo(cx - 10, cy + 2).lineTo(cx + 10, cy + 2).stroke({ width: 2.6, color: W });
        g.circle(cx, cy - 12, 2.5).fill(W);
        break;
      case 'leaf': // pointe vers +x
        g.moveTo(cx - 17, cy).quadraticCurveTo(cx - 2, cy - 15, cx + 19, cy).quadraticCurveTo(cx - 2, cy + 15, cx - 17, cy).fill(W);
        g.moveTo(cx - 21, cy + 2).lineTo(cx + 15, cy).stroke({ width: 1.5, color: 0x9a9a9a });
        for (const d of [-8, -1, 6]) g.moveTo(cx + d, cy).lineTo(cx + d + 5, cy - 5).moveTo(cx + d, cy).lineTo(cx + d + 5, cy + 5).stroke({ width: 1, color: 0xa8a8a8 });
        break;
      case 'petal': // pétale de cerisier encoché, pointe vers +x
        g.moveTo(cx - 14, cy).bezierCurveTo(cx - 6, cy - 14, cx + 12, cy - 14, cx + 16, cy - 5).lineTo(cx + 10, cy).lineTo(cx + 16, cy + 5).bezierCurveTo(cx + 12, cy + 14, cx - 6, cy + 14, cx - 14, cy).fill(W);
        g.ellipse(cx - 8, cy, 5, 3).fill({ color: 0xd8d8d8, alpha: 0.7 });
        break;
      case 'feather': // le long de x
        g.moveTo(cx - 12, cy).bezierCurveTo(cx - 4, cy - 11, cx + 14, cy - 9, cx + 21, cy).bezierCurveTo(cx + 14, cy + 9, cx - 4, cy + 11, cx - 12, cy).fill(W);
        for (let k = 0; k < 4; k++) {
          const x = cx - 3 + k * 6;
          g.moveTo(x, cy).lineTo(x + 5, cy - 7).moveTo(x, cy).lineTo(x + 5, cy + 7).stroke({ width: 1, color: 0xbdbdbd });
        }
        g.moveTo(cx - 21, cy).lineTo(cx + 19, cy).stroke({ width: 1.6, color: 0xc8c8c8 });
        break;
      case 'coin':
        g.circle(cx, cy, 15).fill(0xc4c4c4);
        g.circle(cx, cy, 13).fill(W);
        g.circle(cx, cy, 9.5).stroke({ width: 1.6, color: 0xb4b4b4 });
        g.poly([cx, cy - 5, cx + 4, cy, cx, cy + 5, cx - 4, cy]).fill(0xb4b4b4);
        g.ellipse(cx - 6, cy - 7, 3.5, 2).fill(W);
        break;
      case 'bat': {
        // Vue de dessus, tête vers -y, ailes étendues sur x.
        const half = [3, -10, 4, -4, 10, -7, 17, -9, 22, -3, 18, 0, 15, 3, 12, 1, 8, 5, 5, 3];
        const pts = [cx, cy - 5];
        for (let k = 0; k < half.length; k += 2) pts.push(cx + half[k], cy + half[k + 1]);
        pts.push(cx, cy + 8);
        for (let k = half.length - 2; k >= 0; k -= 2) pts.push(cx - half[k], cy + half[k + 1]);
        g.poly(pts).fill(W);
        g.circle(cx - 1.6, cy - 4, 0.9).circle(cx + 1.6, cy - 4, 0.9).fill(0x303030);
        break;
      }
      case 'butterfly': // vue de dessus, tête vers -y
        for (const s of [-1, 1]) {
          g.moveTo(cx + s, cy - 1).bezierCurveTo(cx + s * 8, cy - 18, cx + s * 22, cy - 15, cx + s * 18, cy - 2).quadraticCurveTo(cx + s * 10, cy + 1, cx + s, cy - 1).fill(W);
          g.moveTo(cx + s, cy + 1).bezierCurveTo(cx + s * 6, cy + 2, cx + s * 17, cy + 6, cx + s * 11, cy + 15).quadraticCurveTo(cx + s * 5, cy + 15, cx + s, cy + 1).fill(W);
          g.circle(cx + s * 12, cy - 7, 3).fill({ color: 0x000000, alpha: 0.28 });
          g.circle(cx + s * 9, cy + 8, 2).fill({ color: 0x000000, alpha: 0.22 });
          g.moveTo(cx + s * 0.8, cy - 9).lineTo(cx + s * 4, cy - 15).stroke({ width: 1, color: 0x606060 });
        }
        g.ellipse(cx, cy + 1, 1.8, 9).fill(0x5a5a5a);
        break;
      case 'pixel':
        g.rect(cx - 14, cy - 14, 28, 28).fill(W);
        break;
      case 'flame': // pointe vers -y
        g.moveTo(cx, cy - 21).bezierCurveTo(cx + 6, cy - 9, cx + 13, cy - 3, cx + 12, cy + 7).bezierCurveTo(cx + 11, cy + 17, cx - 11, cy + 17, cx - 12, cy + 7).bezierCurveTo(cx - 13, cy - 3, cx - 6, cy - 9, cx, cy - 21).fill({ color: W, alpha: 0.5 });
        g.moveTo(cx, cy - 8).bezierCurveTo(cx + 3, cy - 2, cx + 7, cy + 1, cx + 6.5, cy + 7).bezierCurveTo(cx + 6, cy + 13, cx - 6, cy + 13, cx - 6.5, cy + 7).bezierCurveTo(cx - 7, cy + 1, cx - 3, cy - 2, cx, cy - 8).fill(W);
        break;
      case 'lantern':
        g.ellipse(cx, cy, 13, 14).fill(0xdadada);
        g.ellipse(cx, cy, 8, 10).fill(W);
        g.ellipse(cx, cy, 6, 14).stroke({ width: 1.2, color: 0x9a9a9a });
        g.moveTo(cx, cy - 14).lineTo(cx, cy + 14).stroke({ width: 1.2, color: 0x9a9a9a });
        g.rect(cx - 7, cy - 17, 14, 4).fill(0x4a4a4a);
        g.rect(cx - 7, cy + 13, 14, 4).fill(0x4a4a4a);
        g.moveTo(cx, cy - 17).lineTo(cx, cy - 22).stroke({ width: 1.5, color: 0x4a4a4a });
        g.moveTo(cx, cy + 17).lineTo(cx, cy + 23).stroke({ width: 2.2, color: 0xd0d0d0 });
        break;
      case 'ghost':
        g.moveTo(cx - 13, cy + 14)
          .lineTo(cx - 13, cy - 2)
          .arc(cx, cy - 2, 13, Math.PI, Math.PI * 2)
          .lineTo(cx + 13, cy + 14)
          .quadraticCurveTo(cx + 9.75, cy + 20, cx + 6.5, cy + 14)
          .quadraticCurveTo(cx + 3.25, cy + 8, cx, cy + 14)
          .quadraticCurveTo(cx - 3.25, cy + 20, cx - 6.5, cy + 14)
          .quadraticCurveTo(cx - 9.75, cy + 8, cx - 13, cy + 14)
          .fill(W);
        g.ellipse(cx - 5, cy - 3, 2.4, 3.4).ellipse(cx + 5, cy - 3, 2.4, 3.4).fill(0x20202c);
        g.ellipse(cx, cy + 4, 2, 2.6).fill(0x40404c);
        break;
      case 'nut': {
        const hex: number[] = [];
        for (let k = 0; k < 6; k++) hex.push(cx + Math.cos((k / 6) * Math.PI * 2) * 13, cy + Math.sin((k / 6) * Math.PI * 2) * 13);
        g.poly(hex).fill(W).circle(cx, cy, 5.5).cut();
        g.poly(hex).stroke({ width: 1.6, color: 0xb0b0b0 });
        g.circle(cx, cy, 5.5).stroke({ width: 1.6, color: 0x8a8a8a });
        break;
      }
      case 'screw':
        g.rect(cx - 19, cy - 8, 8, 16).fill(W);
        g.rect(cx - 11, cy - 4, 28, 8).fill(0xdcdcdc);
        for (let x = cx - 8; x < cx + 16; x += 4) g.moveTo(x, cy + 4).lineTo(x + 3, cy - 4).stroke({ width: 1.2, color: 0x8a8a8a });
        break;
      case 'jelly':
        g.moveTo(cx - 14, cy + 1)
          .arc(cx, cy + 1, 14, Math.PI, Math.PI * 2)
          .quadraticCurveTo(cx + 10.5, cy + 6, cx + 7, cy + 2)
          .quadraticCurveTo(cx + 3.5, cy + 6, cx, cy + 2)
          .quadraticCurveTo(cx - 3.5, cy + 6, cx - 7, cy + 2)
          .quadraticCurveTo(cx - 10.5, cy + 6, cx - 14, cy + 1)
          .fill({ color: W, alpha: 0.6 });
        g.ellipse(cx, cy - 4, 8, 6).fill({ color: W, alpha: 0.9 });
        for (let k = 0; k < 4; k++) {
          const x = cx - 8 + k * 5.3;
          g.moveTo(x, cy + 3).quadraticCurveTo(x + 4, cy + 8, x, cy + 13).quadraticCurveTo(x - 4, cy + 17, x, cy + 22).stroke({ width: 1.6, color: W, alpha: 0.8 });
        }
        break;
      case 'planet': {
        g.ellipse(cx, cy, 20, 5.5).stroke({ width: 2.2, color: W, alpha: 0.6 });
        g.circle(cx, cy, 10).fill(0x9a9a9a);
        g.circle(cx - 2, cy - 2, 8.6).fill(W);
        const front: number[] = [];
        for (let k = 0; k <= 12; k++) front.push(cx + Math.cos((k / 12) * Math.PI) * 20, cy + Math.sin((k / 12) * Math.PI) * 5.5);
        g.poly(front, false).stroke({ width: 2.2, color: W });
        break;
      }
      case 'orb':
        g.circle(cx, cy, 12).fill(0xa8a8a8);
        g.circle(cx - 2, cy - 2, 10).fill(W);
        break;
      case 'beam': // barre douce le long de x (44 px)
        for (let k = 0; k < 6; k++) {
          const h = 2 + k * 3.2;
          g.roundRect(cx - 22, cy - h / 2, 44, h, h / 2).fill({ color: W, alpha: 0.16 });
        }
        g.roundRect(cx - 21, cy - 1.5, 42, 3, 1.5).fill(W);
        break;
      case 'crystal': // prisme pointu le long de x
        g.poly([cx - 19, cy, cx - 9, cy - 6, cx + 11, cy - 5, cx + 19, cy, cx + 11, cy + 5, cx - 9, cy + 6]).fill(W);
        g.moveTo(cx - 19, cy).lineTo(cx + 19, cy).stroke({ width: 1, color: 0xbcbcbc });
        g.moveTo(cx - 9, cy - 6).lineTo(cx - 5, cy).lineTo(cx - 9, cy + 6).moveTo(cx + 11, cy - 5).lineTo(cx + 7, cy).lineTo(cx + 11, cy + 5).stroke({ width: 1, color: 0xd0d0d0 });
        break;
      case 'flower':
        for (let k = 0; k < 5; k++) {
          const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
          g.circle(cx + Math.cos(a) * 9, cy + Math.sin(a) * 9, 7.5).fill(W);
        }
        for (let k = 0; k < 5; k++) {
          const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
          g.moveTo(cx, cy).lineTo(cx + Math.cos(a) * 13, cy + Math.sin(a) * 13).stroke({ width: 1, color: 0xd4d4d4 });
        }
        g.circle(cx, cy, 4.5).fill(0xbdbdbd);
        break;
      case 'splat':
        g.circle(cx, cy, 11).fill(W);
        for (const [dx, dy, r] of [
          [12, -4, 5.5],
          [-11, 6, 6.5],
          [4, 12, 5],
          [-6, -12, 5.5],
          [16, 9, 3],
          [-18, -6, 3],
          [9, -16, 2.6],
          [-3, 19, 2.6],
          [20, -2, 2],
          [-19, 6, 2],
          [-13, 15, 1.8],
        ])
          g.circle(cx + dx, cy + dy, r).fill(W);
        break;
      // ─── Noël ───
      case 'bulb': // ampoule de guirlande, pointe vers +x, culot sombre à gauche (bout du culot à 16 px du centre)
        g.roundRect(cx - 16, cy - 4.5, 8.5, 9, 1.5).fill(0x4a4a4a);
        for (const d of [-13.5, -11]) g.moveTo(cx + d, cy - 4.5).lineTo(cx + d, cy + 4.5).stroke({ width: 1, color: 0x262626 });
        g.moveTo(cx - 8, cy - 5).bezierCurveTo(cx - 2, cy - 11, cx + 11, cy - 7, cx + 15, cy).bezierCurveTo(cx + 11, cy + 7, cx - 2, cy + 11, cx - 8, cy + 5).fill(W);
        g.ellipse(cx + 3, cy + 3.6, 8, 2.4).fill({ color: 0x000000, alpha: 0.14 });
        break;
      case 'bauble': // boule de Noël (rayon 14, centre 3 px sous la case), éclairée en haut à gauche ; calotte et reflet dans « cap »
        g.circle(cx, cy + 3, 14).fill(0x9a9a9a);
        g.circle(cx - 1.5, cy + 1.5, 12.2).fill(0xcfcfcf);
        g.circle(cx - 3.5, cy - 0.5, 8.5).fill(W);
        g.ellipse(cx, cy + 4, 13.6, 3.4).stroke({ width: 1.2, color: W, alpha: 0.4 });
        break;
      case 'cap': // calotte dorée, anneau et reflets d'une boule, même cadrage que « bauble » (déjà en couleurs : teinte blanche)
        g.circle(cx, cy - 16.8, 2.6).stroke({ width: 1.4, color: 0xc89a2a });
        g.roundRect(cx - 4.5, cy - 14.5, 9, 5.5, 1.2).fill(0xe8b838);
        for (const d of [-2.5, 0, 2.5]) g.moveTo(cx + d, cy - 14).lineTo(cx + d, cy - 9.5).stroke({ width: 0.8, color: 0x9a7414 });
        g.ellipse(cx - 6, cy - 3, 3.6, 2.1).fill({ color: W, alpha: 0.95 });
        g.circle(cx - 2.4, cy - 6.6, 1.2).fill({ color: W, alpha: 0.85 });
        g.moveTo(cx + 8.5, cy + 11).quadraticCurveTo(cx + 12, cy + 7.5, cx + 12.5, cy + 3).stroke({ width: 1.2, color: W, alpha: 0.35 });
        break;
      case 'nstar': {
        // Étoile polaire : quatre longues pointes, quatre courtes, une face de chaque pointe dans l'ombre (relief).
        const at = (k: number) => {
          const a = (k / 16) * Math.PI * 2 - Math.PI / 2;
          const r = k % 2 ? 3.4 : k % 4 === 0 ? 23 : 11;
          return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
        };
        const pts: number[] = [];
        for (let k = 0; k < 16; k++) pts.push(...at(k));
        g.circle(cx, cy, 10).fill({ color: W, alpha: 0.25 });
        g.poly(pts).fill(W);
        for (let k = 0; k < 16; k += 2) g.poly([cx, cy, ...at(k), ...at(k + 1)]).fill(0xb4b4b4);
        break;
      }
      case 'bell': // clochette dorée (déjà en couleurs), ouverture vers +y, battant, petit nœud rouge en haut
        g.moveTo(cx - 3, cy - 12).bezierCurveTo(cx - 10, cy - 11, cx - 10, cy - 1, cx - 11, cy + 6).quadraticCurveTo(cx - 12.5, cy + 9.5, cx - 15, cy + 11).lineTo(cx + 15, cy + 11).quadraticCurveTo(cx + 12.5, cy + 9.5, cx + 11, cy + 6).bezierCurveTo(cx + 10, cy - 1, cx + 10, cy - 11, cx + 3, cy - 12).fill(0xf2c232);
        g.moveTo(cx + 3, cy - 12).bezierCurveTo(cx + 10, cy - 11, cx + 10, cy - 1, cx + 11, cy + 6).quadraticCurveTo(cx + 12.5, cy + 9.5, cx + 15, cy + 11).lineTo(cx + 6, cy + 11).bezierCurveTo(cx + 5.5, cy + 3, cx + 5.5, cy - 6, cx + 3, cy - 12).fill(0xc68a12);
        g.moveTo(cx - 5, cy - 9).bezierCurveTo(cx - 7.5, cy - 5, cx - 7.5, cy + 1, cx - 8.5, cy + 6).stroke({ width: 2, color: 0xfff1b8 });
        g.roundRect(cx - 15.5, cy + 9.5, 31, 3, 1.5).fill(0xd9a020);
        g.circle(cx, cy + 14.5, 3).fill(0x8a5a10);
        g.circle(cx, cy - 13.5, 2.4).fill(0xd9a020);
        g.poly([cx, cy - 15, cx - 8, cy - 20, cx - 7, cy - 12]).fill(0xd42a2a);
        g.poly([cx, cy - 15, cx + 8, cy - 20, cx + 7, cy - 12]).fill(0xd42a2a);
        g.circle(cx, cy - 15, 2.2).fill(0xa81e22);
        break;
      case 'gift': // paquet cadeau ouvert (28 × 22, centré), ombre à droite et sous le couvercle
        g.rect(cx - 14, cy - 11, 28, 22).fill(W);
        g.rect(cx + 7, cy - 11, 7, 22).fill({ color: 0x000000, alpha: 0.16 });
        g.rect(cx - 14, cy - 11, 28, 3).fill({ color: 0x000000, alpha: 0.22 });
        break;
      case 'giftrib': // ruban vertical du paquet, même cadrage que « gift »
        g.rect(cx - 3.5, cy - 11, 7, 22).fill(W);
        g.rect(cx + 1.5, cy - 11, 2, 22).fill({ color: 0x000000, alpha: 0.18 });
        break;
      case 'lid': // couvercle (32 × 8, centré)
        g.rect(cx - 16, cy - 4, 32, 8).fill(W);
        g.rect(cx + 8, cy - 4, 8, 8).fill({ color: 0x000000, alpha: 0.16 });
        g.rect(cx - 16, cy + 2.5, 32, 1.5).fill({ color: 0x000000, alpha: 0.2 });
        break;
      case 'bow': // ruban et nœud du couvercle, même cadrage que « lid »
        g.rect(cx - 3.5, cy - 4, 7, 8).fill(W);
        for (const s of [-1, 1]) {
          g.moveTo(cx, cy - 4).bezierCurveTo(cx + s * 4, cy - 18, cx + s * 16, cy - 13, cx + s, cy - 4).fill(W);
          g.ellipse(cx + s * 6, cy - 9.5, 2.4, 1.6).fill({ color: 0x000000, alpha: 0.22 });
        }
        g.circle(cx, cy - 4.5, 2.8).fill(0xd0d0d0);
        break;
      case 'strip': {
        // Serpentin de papier ondulé le long de x.
        const pts: number[] = [];
        for (let k = 0; k <= 12; k++) pts.push(cx - 18 + k * 3, cy + Math.sin(k * 0.9) * 4 - 2.2);
        for (let k = 12; k >= 0; k--) pts.push(cx - 18 + k * 3, cy + Math.sin(k * 0.9) * 4 + 2.2);
        g.poly(pts).fill(W);
        break;
      }
      case 'chunk': {
        // Motte de neige irrégulière, ombrée en bas à droite.
        const pts: number[] = [];
        [12, 10, 13, 9.5, 12.5, 10.5, 13, 9].forEach((v, k) => pts.push(cx + Math.cos((k / 8) * Math.PI * 2) * v, cy + Math.sin((k / 8) * Math.PI * 2) * v));
        g.poly(pts).fill(0xb4b4b4);
        g.circle(cx - 2, cy - 2, 8.5).fill(W);
        g.circle(cx + 3.5, cy - 4.5, 5).fill(W);
        g.circle(cx - 5, cy + 3, 4.5).fill(W);
        break;
      }
      case 'snowball': // boule de neige (déjà en couleurs : ombre bleutée, grain)
        g.circle(cx, cy, 20).fill(0xa9bccf);
        g.circle(cx - 2, cy - 2, 17.5).fill(0xd6e2ee);
        g.circle(cx - 5, cy - 5, 12).fill(0xf6faff);
        for (const [dx, dy, rr] of [
          [8, 6, 2.2],
          [-9, 9, 1.8],
          [3, 12, 1.5],
          [12, -3, 1.6],
          [-12, -2, 1.3],
        ])
          g.circle(cx + dx, cy + dy, rr).fill({ color: 0x9fb4c8, alpha: 0.6 });
        break;
      case 'snowman': {
        // Bonhomme de neige de face (déjà en couleurs), pieds au bas de la case (22 px sous le centre).
        const ball = (bx: number, by: number, br: number) => {
          g.circle(bx, by, br).fill(0xb9cbdc);
          g.circle(bx - br * 0.15, by - br * 0.15, br * 0.86).fill(0xe4edf6);
          g.circle(bx - br * 0.32, by - br * 0.32, br * 0.55).fill(W);
        };
        g.moveTo(cx - 5, cy - 1).lineTo(cx - 16, cy - 7).moveTo(cx - 12.5, cy - 5).lineTo(cx - 14, cy - 10).moveTo(cx + 5, cy - 1).lineTo(cx + 16, cy - 7).moveTo(cx + 12.5, cy - 5).lineTo(cx + 15.5, cy - 9.5).stroke({ width: 1.6, color: 0x6b4a2b });
        ball(cx, cy + 12, 10);
        ball(cx, cy - 0.5, 7.5);
        ball(cx, cy - 11, 5.5);
        g.roundRect(cx - 5.5, cy - 7.2, 11, 2.8, 1.4).fill(0xd42a2a);
        g.roundRect(cx + 1.5, cy - 6, 3, 7, 1).fill(0xb81f22);
        for (const by of [-1.5, 2.5, 9, 13.5]) g.circle(cx, cy + by, 1).fill(0x26262c);
        g.circle(cx - 2, cy - 12.2, 0.9).circle(cx + 2, cy - 12.2, 0.9).fill(0x1a1a1f);
        g.poly([cx - 0.3, cy - 11, cx + 6.5, cy - 9.6, cx - 0.3, cy - 9]).fill(0xff7a1a);
        g.rect(cx - 7.5, cy - 17.5, 15, 2).fill(0x1e1e24);
        g.rect(cx - 4.5, cy - 23.5, 9, 6.5).fill(0x26262e);
        g.rect(cx - 4.5, cy - 19.4, 9, 1.6).fill(0xd42a2a);
        break;
      }
    }
  });
  const rows = Math.ceil(TEXS.length / COLS);
  const rt = renderer.generateTexture({ target: g, frame: new Rectangle(0, 0, COLS * CELL, rows * CELL), resolution: 2 });
  g.destroy();
  const out = {} as Record<Tex, Texture>;
  TEXS.forEach((t, i) => (out[t] = new Texture({ source: rt.source, frame: new Rectangle((i % COLS) * CELL, Math.floor(i / COLS) * CELL, CELL, CELL) })));
  return out;
}

interface Orbit {
  c: { x: number; y: number };
  a: number;
  r: number;
  w: number;
  /** Variation du rayon par seconde (spirales). */
  dr?: number;
  /** Aplatissement vertical (tornade). */
  sq?: number;
}

interface Live {
  p: Particle;
  layer: ParticleContainer;
  tex: Tex;
  vx: number;
  vy: number;
  life: number;
  max: number;
  spin: number;
  drag: number;
  grav: number;
  s0: number;
  s1: number;
  /** Échelle fixe le long de x (barres, rubans), sinon celle de la taille. */
  lx?: number;
  a0: number;
  /** Orbite autour d'un point suivi (auras) : angle, rayon, vitesse angulaire. */
  orbit?: Orbit;
  /** Décalage ajouté à l'orbite (intégré avec vx/vy : méduses qui montent, tornade). */
  ox: number;
  oy: number;
  /** Rayon d'orbite en rayons de tank (effets permanents, suivent la taille du tank). */
  rk?: number;
  /** 1 = apparition en fondu (lucioles, flocons). */
  fadeIn?: boolean;
  /** Opaque jusqu'au dernier quart de la vie (flaques, tapis). */
  hold?: boolean;
  /** Grossit vite puis ralentit (éclosions, ondes). */
  pop?: boolean;
  /** Teinte qui passe de tint à tint2 pendant la vie (braises). */
  tint2?: number;
  tint: number;
  /** Orientation sur la direction du mouvement, plus ce décalage (rad). */
  face?: number;
  /** Battement (ailes, pièce qui tourne) : écrasement de x à cette pulsation. */
  flap?: number;
  /** Clignotement (pixels) à cette pulsation. */
  blink?: number;
  /** Pulsation de la taille (méduses). */
  pulse?: number;
  /** Balancement latéral (feuilles, pétales) : vitesse max et pulsation. */
  sway?: { a: number; w: number };
  ph: number;
  /** Sol (y) sur lequel la particule rebondit (pièces, boulons, sable). */
  floor?: number;
  bounce: number;
  /** Appelé à chaque image (traînées de météores, voiles de fantômes). */
  emit?: (l: Live, dt: number) => void;
  /** Appelé à la fin de la vie (impact des météores). */
  onEnd?: (l: Live) => void;
}

interface SpawnOpts {
  tex: Tex;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  life: number;
  size: number;
  size1?: number;
  /** Échelle fixe le long de x, mêmes unités que size (voir BEAM, PIX, ORB pour une longueur affichée). */
  len?: number;
  tint: number;
  tint2?: number;
  alpha?: number;
  spin?: number;
  rot?: number;
  drag?: number;
  grav?: number;
  add?: boolean;
  below?: boolean;
  fadeIn?: boolean;
  hold?: boolean;
  pop?: boolean;
  orbit?: Orbit;
  ox?: number;
  oy?: number;
  rk?: number;
  face?: number;
  flap?: number;
  blink?: number;
  pulse?: number;
  sway?: { a: number; w: number };
  ph?: number;
  floor?: number;
  bounce?: number;
  emit?: (l: Live, dt: number) => void;
  onEnd?: (l: Live) => void;
}

interface Bolt {
  g: Graphics;
  life: number;
  max: number;
}

const TAU = Math.PI * 2;
const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const sign = () => (Math.random() < 0.5 ? -1 : 1);
const lerpColor = (a: number, b: number, t: number) => {
  const ch = (c: number, s: number) => (c >> s) & 255;
  const m = (s: number) => Math.round(ch(a, s) + (ch(b, s) - ch(a, s)) * t) << s;
  return m(16) | m(8) | m(0);
};
function hsl(h: number, s: number, l: number): number {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return (Math.round(f(0) * 255) << 16) | (Math.round(f(8) * 255) << 8) | Math.round(f(4) * 255);
}

/** Position et vitesse d'un tank pour ses effets continus. */
export interface FxTank {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Rayon affiché (unités du monde). */
  r: number;
  /** Direction de la caisse (rad), pour placer l'arrière de la traînée. */
  hull: number;
}

const MAX_LIVE = 1400;
/** Les tailles passées aux émetteurs sont en rayons de tank ; la texture occupe une partie de sa case. */
const SIZE_K = 1.4;
/** Facteurs « longueur affichée → len/size » des textures allongées (barre 44 px, pixel 28 px, bille 24 px). */
const BEAM = CELL / (SIZE_K * 44);
const PIX = CELL / (SIZE_K * 28);
const ORB = CELL / (SIZE_K * 24);

const ICE = [0xffffff, 0xd8f2ff, 0xb8e4ff];
const SAND = [0xe6c88a, 0xd4a960, 0xf2dcae, 0xc89b52];
const GOLD = [0xffc93c, 0xffd966, 0xe8a317];
const SAKURA = [0xffc4da, 0xffa6c6, 0xffe3ee, 0xff8fb8];
const CYBER = [0x39ff8f, 0x3dfcff, 0x9dffcf];
const INK = [0x120a1c, 0x2d1747, 0x4a2a78];
// Noël : ampoules de guirlande, boules du sapin, papier de papillote et de cadeau, neige.
const XMAS_BULBS = [0xff3b3b, 0x3ddc5a, 0xffcc33, 0x3d8bff];
const XMAS_BALLS = [0xe0283a, 0xf5b82e, 0x2f72e8, 0x22a552, 0xd8dee8];
const XMAS_PAPER = [0xff3b4f, 0x2fd06a, 0xffd34d, 0x4f9dff, 0xffffff, 0xff7ad0];
const SNOW = [0xffffff, 0xeef6ff, 0xdcebff];

/** Échelle d'une particule pour une taille donnée en unités du monde (voir SIZE_K). */
const scaleOf = (v: number) => (v * SIZE_K) / CELL;
/** Grossit en dépassant un peu sa taille puis s'y pose (0 → 1). */
const backOut = (u: number) => 1 + 2.70158 * (u - 1) ** 3 + 1.70158 * (u - 1) ** 2;

export class Fx {
  readonly root = new Container();
  /** Sous les tanks (traînées), au-dessus (auras, tirs, éliminations). */
  readonly below = new Container();
  private readonly tex: Record<Tex, Texture>;
  private readonly addB: ParticleContainer;
  private readonly addA: ParticleContainer;
  private readonly normB: ParticleContainer;
  private readonly normA: ParticleContainer;
  private readonly bolts = new Container();
  private live: Live[] = [];
  private boltList: Bolt[] = [];
  private acc = new Map<string, number>();
  /** Dernier point arrière de chaque traînée : les particules sont réparties le long du trajet (ruban continu). */
  private lastRear = new Map<string, { x: number; y: number }>();
  /** Dernier point posé du tapis rouge (ruban en ligne brisée), par tank. */
  private ribbon = new Map<string, { x: number; y: number }>();
  /** Particules permanentes d'une aura (runes, satellites, pièces, lanternes, halo), par tank. */
  private persist = new Map<string, { id: string; ls: Live[] }>();
  /** Étapes différées des effets ponctuels (salves, impacts, éclatements). */
  private timers: { t: number; fn: () => void }[] = [];
  private time = 0;
  /** 0,3 à 1 selon le réglage « particules » ; 0 coupe les effets cosmétiques. */
  budget = 1;

  constructor(renderer: Renderer) {
    this.tex = buildAtlas(renderer);
    const mk = (blendMode: 'add' | 'normal') =>
      new ParticleContainer({ dynamicProperties: { position: true, rotation: true, vertex: true, color: true }, blendMode });
    this.normB = mk('normal');
    this.addB = mk('add');
    this.normA = mk('normal');
    this.addA = mk('add');
    this.below.addChild(this.normB, this.addB);
    this.bolts.blendMode = 'add';
    this.root.addChild(this.normA, this.addA, this.bolts);
  }

  private spawn(o: SpawnOpts): Live | null {
    if (this.live.length >= MAX_LIVE) return null;
    const layer = o.below ? (o.add ? this.addB : this.normB) : o.add ? this.addA : this.normA;
    const s0 = (o.size * SIZE_K) / CELL;
    const lx = o.len !== undefined ? (o.len * SIZE_K) / CELL : undefined;
    const p = new Particle({ texture: this.tex[o.tex], x: o.x, y: o.y, anchorX: 0.5, anchorY: 0.5, scaleX: lx ?? s0, scaleY: s0, rotation: o.rot ?? 0, tint: o.tint, alpha: o.fadeIn ? 0 : (o.alpha ?? 1) });
    layer.addParticle(p);
    const l: Live = {
      p,
      layer,
      tex: o.tex,
      vx: o.vx ?? 0,
      vy: o.vy ?? 0,
      life: o.life,
      max: o.life,
      spin: o.spin ?? 0,
      drag: o.drag ?? 1,
      grav: o.grav ?? 0,
      s0,
      s1: ((o.size1 ?? o.size) * SIZE_K) / CELL,
      lx,
      a0: o.alpha ?? 1,
      orbit: o.orbit,
      ox: o.ox ?? 0,
      oy: o.oy ?? 0,
      rk: o.rk,
      fadeIn: o.fadeIn,
      hold: o.hold,
      pop: o.pop,
      tint: o.tint,
      tint2: o.tint2,
      face: o.face,
      flap: o.flap,
      blink: o.blink,
      pulse: o.pulse,
      sway: o.sway,
      ph: o.ph ?? rnd(0, TAU),
      floor: o.floor,
      bounce: o.bounce ?? 0.4,
      emit: o.emit,
      onEnd: o.onEnd,
    };
    this.live.push(l);
    return l;
  }

  /** Barre lumineuse d'un point à un autre (lasers, rayons, colonnes, lignes de balayage). */
  private line(x0: number, y0: number, x1: number, y1: number, o: Omit<SpawnOpts, 'tex' | 'x' | 'y' | 'rot' | 'len'>): Live | null {
    return this.spawn({ tex: 'beam', x: (x0 + x1) / 2, y: (y0 + y1) / 2, rot: Math.atan2(y1 - y0, x1 - x0), len: Math.hypot(x1 - x0, y1 - y0) * BEAM, add: true, ...o });
  }

  /** Replace une particule permanente « pixel » (carré de 28 px) en barre pleine d'un point à un autre, épaisseur w
   *  (fil de la guirlande) : posée à la main à chaque image, sans orbite. */
  private seg(l: Live, x0: number, y0: number, x1: number, y1: number, w: number): void {
    l.p.x = (x0 + x1) / 2;
    l.p.y = (y0 + y1) / 2;
    l.p.rotation = Math.atan2(y1 - y0, x1 - x0);
    l.lx = (Math.hypot(x1 - x0, y1 - y0) + w * 0.8) / 28;
    l.s0 = l.s1 = w / 28;
  }

  /** Nombre d'émissions dues pour une clé à un débit donné (par seconde), budget compris. */
  private due(key: string, rate: number, dt: number): number {
    const v = (this.acc.get(key) ?? 0) + rate * this.budget * dt;
    const n = Math.floor(v);
    this.acc.set(key, v - n);
    return n;
  }

  /** Émetteur attaché à une particule (débit par seconde, budget compris). */
  private emitter(rate: number, mk: (l: Live) => void): (l: Live, dt: number) => void {
    let acc = 0;
    return (l, dt) => {
      acc += rate * this.budget * dt;
      for (; acc >= 1; acc--) mk(l);
    };
  }

  private later(t: number, fn: () => void): void {
    this.timers.push({ t, fn });
  }

  private bolt(x0: number, y0: number, x1: number, y1: number, color: number, width = 2, life = 0.14): void {
    if (this.budget <= 0) return;
    const g = new Graphics();
    const pts = [x0, y0];
    const n = 7;
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const nx = -(y1 - y0);
      const ny = x1 - x0;
      const len = Math.hypot(nx, ny) || 1;
      const j = rnd(-0.12, 0.12) * Math.hypot(x1 - x0, y1 - y0);
      pts.push(x0 + (x1 - x0) * t + (nx / len) * j, y0 + (y1 - y0) * t + (ny / len) * j);
    }
    pts.push(x1, y1);
    g.poly(pts, false).stroke({ width: width * 3, color, alpha: 0.3 }).stroke({ width, color: 0xffffff });
    this.bolts.addChild(g);
    this.boltList.push({ g, life, max: life });
  }

  // ─── Effets continus ──────────────────────────────────────────────────

  /** Particules permanentes d'une aura : créées une fois, retirées quand l'aura change ou que le tank disparaît. */
  private keep(key: string, id: string, make: () => (Live | null)[]): Live[] {
    const cur = this.persist.get(key);
    if (cur && cur.id === id) return cur.ls;
    this.clearPersist(key);
    const ls = make().filter((l): l is Live => !!l);
    this.persist.set(key, { id, ls });
    return ls;
  }

  /** Le rayon des orbites permanentes suit la taille du tank (montée de niveau). */
  private track(ls: Live[], r: number): void {
    for (const l of ls) if (l.orbit && l.rk !== undefined) l.orbit.r = r * l.rk;
  }

  /** Aura autour d'un tank ; `key` identifie le tank (accumulateurs d'émission). */
  aura(key: string, id: string | null, t: FxTank, dt: number): void {
    if (!id || this.budget <= 0) {
      this.clearPersist(key);
      return;
    }
    const r = t.r;
    const k = `a:${key}`;
    switch (id) {
      case 'aura:etincelles':
        for (let i = this.due(k, 20, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.95, 1.15);
          this.spawn({ tex: 'spark', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: Math.cos(a) * 30 - Math.sin(a) * 40, vy: Math.sin(a) * 30 + Math.cos(a) * 40, rot: a + Math.PI / 2, life: rnd(0.25, 0.5), size: r * 0.5, size1: r * 0.2, tint: Math.random() < 0.5 ? 0xffd36b : 0xfff4d6, add: true, drag: 0.9 });
        }
        break;
      case 'aura:flocons':
        for (let i = this.due(k, 7, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.9, 1.3);
          this.spawn({ tex: 'flake', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.6 + rnd(-8, 8), vy: t.vy * 0.6 + rnd(6, 16), life: rnd(1.2, 2), size: r * rnd(0.22, 0.38), tint: Math.random() < 0.4 ? 0xbfe6ff : 0xffffff, spin: rnd(-1.5, 1.5), fadeIn: true, alpha: 0.95 });
        }
        break;
      case 'aura:braises':
        for (let i = this.due(k, 16, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.7, 1.15);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.7 + rnd(-10, 10), vy: t.vy * 0.7 - rnd(25, 55), life: rnd(0.6, 1.1), size: r * rnd(0.25, 0.45), size1: r * 0.08, tint: 0xffe08a, tint2: 0xff3a12, add: true });
        }
        break;
      case 'aura:lucioles':
        for (let i = this.due(k, 3.5, dt); i > 0; i--) {
          this.spawn({ tex: 'dot', x: t.x, y: t.y, life: rnd(1.8, 2.8), size: r * rnd(0.3, 0.45), tint: Math.random() < 0.5 ? 0xd9ff6b : 0x9dffb0, add: true, fadeIn: true, orbit: { c: t, a: rnd(0, Math.PI * 2), r: r * rnd(1.05, 1.4), w: rnd(-1.4, 1.4) } });
        }
        break;
      case 'aura:orage':
        // Jaune électrique, loin du tank, 2,5 éclairs par seconde : jamais confondu avec le bouclier (bleu).
        if (this.due(`${k}:b`, 2.5, dt) > 0) {
          const a = rnd(0, Math.PI * 2);
          const a2 = a + rnd(0.6, 1.6);
          this.bolt(t.x + Math.cos(a) * r * 1.5, t.y + Math.sin(a) * r * 1.5, t.x + Math.cos(a2) * r * 1.55, t.y + Math.sin(a2) * r * 1.55, 0xffe45c, 1.4);
        }
        for (let i = this.due(k, 8, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * r * 1.5, y: t.y + Math.sin(a) * r * 1.5, life: 0.3, size: r * 0.3, size1: 0, tint: 0xfff3a8, add: true });
        }
        break;
      case 'aura:runes': {
        // Anneau permanent de huit runes qui tournent lentement autour du tank.
        const ring = this.keep(key, id, () => Array.from({ length: 8 }, (_, i) => this.spawn({ tex: 'rune', x: t.x, y: t.y, life: 1e9, size: r * 0.42, tint: 0xc88cff, add: true, orbit: { c: t, a: (i / 8) * TAU, r: r * 1.3, w: 0.6 }, rk: 1.3 })));
        this.track(ring, r);
        for (const l of ring) l.p.alpha = 0.55 + 0.45 * Math.sin(this.time * 3 + l.orbit!.a * 2);
        for (let i = this.due(k, 6, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * r * 1.3, y: t.y + Math.sin(a) * r * 1.3, vy: -15, life: 0.8, size: r * 0.25, size1: 0, tint: 0xe2c2ff, add: true });
        }
        break;
      }
      case 'aura:blizzard':
        // Flocons et rafales blanches qui tournent vite et serré (spirale qui se resserre).
        for (let i = this.due(k, 12, dt); i > 0; i--) {
          this.spawn({ tex: 'flake', x: t.x, y: t.y, life: rnd(0.9, 1.4), size: r * rnd(0.26, 0.4), size1: r * 0.12, tint: Math.random() < 0.35 ? 0xd4efff : 0xffffff, spin: rnd(-3, 3), fadeIn: true, orbit: { c: t, a: rnd(0, TAU), r: r * rnd(1.1, 1.45), w: rnd(3, 4.2), dr: -r * 0.25 } });
        }
        for (let i = this.due(`${k}:b`, 8, dt); i > 0; i--) {
          this.spawn({ tex: 'spark', x: t.x, y: t.y, life: rnd(0.35, 0.55), size: r * rnd(0.7, 1), size1: r * 0.3, tint: 0xeef8ff, alpha: 0.85, add: true, fadeIn: true, face: 0, orbit: { c: t, a: rnd(0, TAU), r: r * rnd(1.15, 1.5), w: rnd(4, 5) } });
        }
        break;
      case 'aura:cendres':
        // Flocons de cendre grise qui montent en se balançant, quelques braises.
        for (let i = this.due(k, 10, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(0.85, 1.25);
          this.spawn({ tex: 'shard', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.6 + rnd(-8, 8), vy: t.vy * 0.6 - rnd(12, 28), life: rnd(1.3, 2), size: r * rnd(0.22, 0.34), tint: pick([0xa8a29b, 0xc4beb6, 0x8d8882, 0xd8d2ca]), spin: rnd(-4, 4), sway: { a: 14, w: 3 }, fadeIn: true, alpha: 0.95 });
        }
        for (let i = this.due(`${k}:b`, 4, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(0.85, 1.2);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.7, vy: t.vy * 0.7 - rnd(30, 50), life: rnd(0.7, 1.1), size: r * 0.24, size1: r * 0.05, tint: 0xffc070, tint2: 0xff3a12, add: true });
        }
        break;
      case 'aura:donnees':
        // Pixels verts et cyan qui montent en clignotant, ligne de balayage qui passe sur le tank.
        for (let i = this.due(k, 13, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(0.75, 1.3);
          const s = r * rnd(0.14, 0.24) * PIX;
          this.spawn({ tex: 'pixel', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.8, vy: t.vy * 0.8 - rnd(25, 45), life: rnd(0.6, 1.1), size: s, tint: pick(CYBER), add: true, blink: rnd(14, 22) });
        }
        if (this.due(`${k}:b`, 1.1, dt) > 0) this.spawn({ tex: 'beam', x: t.x, y: t.y, life: 0.9, size: r * 0.14, len: r * 2.3 * BEAM, tint: 0x3dfcff, alpha: 0.55, add: true, fadeIn: true, orbit: { c: t, a: Math.PI / 2, r: r * 1.1, w: 0, dr: (-r * 2.2) / 0.9 } });
        break;
      case 'aura:papillons':
        // Quelques papillons colorés qui volettent en orbite (ailes qui battent), un peu de poussière dorée.
        for (let i = this.due(k, 2, dt); i > 0; i--) {
          this.spawn({ tex: 'butterfly', x: t.x, y: t.y, life: rnd(2.6, 3.6), size: r * rnd(0.5, 0.62), tint: pick([0xff7ab6, 0xffd23f, 0x7de0ff, 0xb388ff, 0xff8c42, 0x8cff7a]), fadeIn: true, flap: rnd(14, 18), face: Math.PI / 2, sway: { a: r * 0.9, w: rnd(4, 6) }, orbit: { c: t, a: rnd(0, TAU), r: r * rnd(1.2, 1.6), w: rnd(0.8, 1.3) * sign() } });
        }
        for (let i = this.due(`${k}:b`, 4, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(1.1, 1.6);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.8, vy: t.vy * 0.8, life: 0.5, size: r * 0.18, size1: 0, tint: 0xfff6c8, add: true });
        }
        break;
      case 'aura:meduses':
        // Petites méduses lumineuses qui pulsent en dérivant lentement autour du tank.
        for (let i = this.due(k, 1.5, dt); i > 0; i--) {
          const tint = pick([0xff8ae2, 0xc59bff, 0x8affe6]);
          this.spawn({
            tex: 'jelly',
            x: t.x,
            y: t.y,
            vy: -rnd(6, 12),
            life: rnd(2.8, 3.6),
            size: r * rnd(0.55, 0.68),
            tint,
            alpha: 0.9,
            add: true,
            fadeIn: true,
            pulse: rnd(4, 5.5),
            orbit: { c: t, a: rnd(0, TAU), r: r * rnd(1.25, 1.55), w: rnd(0.3, 0.5) * sign() },
            emit: this.emitter(2.2, (l) => this.spawn({ tex: 'dot', x: l.p.x + rnd(-3, 3), y: l.p.y + r * 0.2, vx: t.vx * 0.5, vy: t.vy * 0.5 + rnd(5, 15), life: 0.6, size: r * 0.14, size1: 0, tint, add: true })),
          });
        }
        break;
      case 'aura:satellites': {
        // Une planète à anneau et deux lunes en orbite, chacune avec un fin sillage.
        const ls = this.keep(key, id, () => [
          this.spawn({ tex: 'planet', x: t.x, y: t.y, life: 1e9, size: r * 0.72, tint: 0xffc27a, rot: -0.4, orbit: { c: t, a: 0, r: r * 1.6, w: 1.1 }, rk: 1.6 }),
          this.spawn({ tex: 'orb', x: t.x, y: t.y, life: 1e9, size: r * 0.4, tint: 0xd8dde6, orbit: { c: t, a: 2.1, r: r * 1.25, w: -1.7 }, rk: 1.25 }),
          this.spawn({ tex: 'orb', x: t.x, y: t.y, life: 1e9, size: r * 0.3, tint: 0xff7aa0, orbit: { c: t, a: 4.2, r: r * 1.95, w: 0.7 }, rk: 1.95 }),
        ]);
        this.track(ls, r);
        const m = this.due(k, 18, dt);
        for (let i = 0; i < m && ls.length; i++) {
          const s = pick(ls);
          this.spawn({ tex: 'dot', x: s.p.x, y: s.p.y, life: 0.55, size: r * (s.tex === 'planet' ? 0.36 : 0.26), size1: 0, tint: s.tint, alpha: 0.75, add: true });
        }
        break;
      }
      case 'aura:tresor': {
        // Six pièces d'or qui tournent sur elles-mêmes en orbite, éclats de lumière sur les pièces.
        const ls = this.keep(key, id, () => Array.from({ length: 6 }, (_, i) => this.spawn({ tex: 'coin', x: t.x, y: t.y, life: 1e9, size: r * 0.46, tint: 0xffc93c, flap: 4.5, ph: i * 0.9, orbit: { c: t, a: (i / 6) * TAU, r: r * 1.42, w: 0.9 }, rk: 1.42 })));
        this.track(ls, r);
        for (let i = this.due(k, 6, dt); i > 0 && ls.length; i--) {
          const s = pick(ls);
          this.spawn({ tex: 'star', x: s.p.x + rnd(-3, 3), y: s.p.y + rnd(-3, 3), life: 0.35, size: r * 0.32, size1: 0, tint: 0xfff6c8, spin: 4, add: true });
        }
        break;
      }
      case 'aura:radioactif':
        // Lueurs vert-jaune toxiques par plaques (jamais un disque plein), particules et bulles qui montent.
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          this.spawn({ tex: 'smoke', x: t.x, y: t.y, life: 1.2, size: r * 1.1, size1: r * 1.5, tint: 0x9dff2e, alpha: 0.22, add: true, fadeIn: true, orbit: { c: t, a: rnd(0, TAU), r: r * rnd(0.7, 0.95), w: rnd(-0.6, 0.6) } });
        }
        for (let i = this.due(k, 10, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(0.8, 1.25);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.7, vy: t.vy * 0.7 - rnd(20, 40), life: rnd(0.7, 1.1), size: r * 0.32, size1: r * 0.06, tint: Math.random() < 0.6 ? 0xb6ff3a : 0x6dff5a, add: true, sway: { a: 12, w: 5 } });
        }
        for (let i = this.due(`${k}:c`, 2, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(0.8, 1.2);
          this.spawn({ tex: 'bubble', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.7, vy: t.vy * 0.7 - 20, life: 0.6, size: r * 0.14, size1: r * 0.3, tint: 0xb8ff6a, alpha: 0.85 });
        }
        break;
      case 'aura:ames':
        // Âmes pâles qui tournoient (la queue suit le mouvement) et laissent un voile lumineux.
        for (let i = this.due(k, 1.4, dt); i > 0; i--) {
          this.spawn({
            tex: 'ghost',
            x: t.x,
            y: t.y,
            life: rnd(2.4, 3.2),
            size: r * rnd(0.56, 0.66),
            tint: pick([0xeef4ff, 0xdde8ff, 0xe8dcff]),
            alpha: 0.8,
            fadeIn: true,
            face: Math.PI / 2,
            orbit: { c: t, a: rnd(0, TAU), r: r * rnd(1.3, 1.6), w: rnd(1.5, 2.1) * sign() },
            emit: this.emitter(4, (l) => this.spawn({ tex: 'smoke', x: l.p.x, y: l.p.y, vx: t.vx * 0.3, vy: t.vy * 0.3, life: 0.8, size: r * 0.4, size1: r * 0.7, tint: 0xcfdcff, alpha: 0.2, add: true })),
          });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(1.1, 1.7);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.8, vy: t.vy * 0.8 - 12, life: 0.9, size: r * 0.2, size1: 0, tint: 0xe6eeff, alpha: 0.7, add: true, fadeIn: true });
        }
        break;
      case 'aura:lanternes': {
        // Quatre lanternes rouges qui flottent en orbite lente, halo orangé qui vacille.
        const ls = this.keep(key, id, () => {
          const out: (Live | null)[] = [];
          for (let i = 0; i < 4; i++) {
            const orbit = (): Orbit => ({ c: t, a: (i / 4) * TAU + 0.4, r: r * 1.6, w: 0.35 });
            out.push(this.spawn({ tex: 'dot', x: t.x, y: t.y, life: 1e9, size: r * 0.95, tint: 0xff7a2a, alpha: 0.5, add: true, orbit: orbit(), rk: 1.6 }));
            out.push(this.spawn({ tex: 'lantern', x: t.x, y: t.y, life: 1e9, size: r * 0.54, tint: 0xff3b2f, orbit: orbit(), rk: 1.6 }));
          }
          return out;
        });
        this.track(ls, r);
        for (const l of ls) {
          if (l.tex === 'lantern') l.p.rotation = Math.sin(this.time * 1.6 + l.ph) * 0.16;
          else l.p.alpha = 0.42 + 0.14 * Math.sin(this.time * 7 + l.ph) + 0.06 * Math.sin(this.time * 17 + l.ph);
        }
        for (let i = this.due(k, 4, dt); i > 0 && ls.length; i--) {
          const s = pick(ls);
          this.spawn({ tex: 'dot', x: s.p.x + rnd(-3, 3), y: s.p.y - r * 0.2, vx: t.vx * 0.7, vy: t.vy * 0.7 - rnd(20, 35), life: 0.7, size: r * 0.16, size1: 0, tint: 0xffc070, add: true });
        }
        break;
      }
      case 'aura:sable':
        // Grains et traits de sable en tourbillon qui s'élargit, voile de poussière.
        for (let i = this.due(k, 17, dt); i > 0; i--) {
          const streak = Math.random() < 0.45;
          this.spawn({ tex: streak ? 'spark' : 'orb', x: t.x, y: t.y, life: rnd(0.6, 1), size: streak ? r * rnd(0.6, 0.85) : r * rnd(0.14, 0.22), size1: streak ? r * 0.3 : undefined, tint: pick(SAND), add: streak, fadeIn: true, face: streak ? 0 : undefined, orbit: { c: t, a: rnd(0, TAU), r: r * rnd(0.95, 1.4), w: rnd(3.4, 4.6), dr: r * 0.35 } });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          this.spawn({ tex: 'smoke', x: t.x, y: t.y, life: 1, size: r * 0.8, size1: r * 1.2, tint: 0xe0c08a, alpha: 0.45, fadeIn: true, orbit: { c: t, a: rnd(0, TAU), r: r * 1.2, w: 2.5 } });
        }
        break;
      case 'aura:halo': {
        // Anneau doré permanent qui respire, rayons qui scintillent tout autour, étincelles dorées.
        const ls = this.keep(key, id, () => {
          const out: (Live | null)[] = [
            this.spawn({ tex: 'ring', x: t.x, y: t.y, life: 1e9, size: r * 2.25, tint: 0xffd76a, alpha: 0.85, add: true, orbit: { c: t, a: 0, r: 0, w: 0 }, rk: 0 }),
            this.spawn({ tex: 'ring', x: t.x, y: t.y, life: 1e9, size: r * 2.7, tint: 0xfff1c0, alpha: 0.18, add: true, orbit: { c: t, a: 0, r: 0, w: 0 }, rk: 0 }),
          ];
          for (let i = 0; i < 10; i++) out.push(this.spawn({ tex: 'beam', x: t.x, y: t.y, life: 1e9, size: r * 0.14, len: r * 0.55 * BEAM, tint: 0xffe39a, add: true, face: -Math.PI / 2, ph: i * 1.7, orbit: { c: t, a: (i / 10) * TAU, r: r * 1.8, w: 0.25 }, rk: 1.8 }));
          return out;
        });
        this.track(ls, r);
        for (const l of ls) {
          if (l.tex === 'ring') l.p.alpha = l.a0 * (0.75 + 0.25 * Math.sin(this.time * 2.4));
          else l.p.alpha = 0.2 + 0.4 * (0.5 + 0.5 * Math.sin(this.time * 3 + l.ph));
        }
        for (let i = this.due(k, 8, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const rr = r * rnd(1.15, 1.5);
          this.spawn({ tex: 'star', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.8, vy: t.vy * 0.8 - rnd(10, 25), life: rnd(0.5, 0.8), size: r * 0.3, size1: 0, tint: pick([0xffe07a, 0xfff6d0]), spin: 3, add: true });
        }
        break;
      }
      case 'aura:guirlande': {
        // Guirlande lumineuse : un fil vert festonné tout autour du tank, huit ampoules (rouge, vert, or, bleu) au creux
        // des festons, qui s'allument à tour de rôle (chenillard) ; le tout tourne lentement et le fil ondule.
        // Particules permanentes posées à la main à chaque image ; l.ph donne leur rôle (fil 0-15, ampoule 100+, lueur 200+).
        const N = 8;
        const ls = this.keep(key, id, () => {
          const out: (Live | null)[] = [];
          for (let i = 0; i < N * 2; i++) out.push(this.spawn({ tex: 'pixel', x: t.x, y: t.y, life: 1e9, size: 0, tint: 0x2a5a30, ph: i }));
          for (let i = 0; i < N; i++) {
            out.push(this.spawn({ tex: 'bulb', x: t.x, y: t.y, life: 1e9, size: r * 0.42, tint: XMAS_BULBS[i % 4], ph: 100 + i }));
            out.push(this.spawn({ tex: 'dot', x: t.x, y: t.y, life: 1e9, size: r * 0.7, tint: XMAS_BULBS[i % 4], alpha: 0.5, add: true, ph: 200 + i }));
          }
          return out;
        });
        const spin = this.time * 0.4;
        const sag = r * (1.56 + 0.05 * Math.sin(this.time * 1.9));
        // Point j du fil, en demi-pas : pair = accroche (près du tank), impair = creux du feston (où pend l'ampoule).
        const pt = (j: number) => {
          const a = spin + (j / (N * 2)) * TAU;
          const d = j % 2 ? sag + r * 0.04 * Math.sin(this.time * 2.7 + j) : r * 1.3;
          return { a, x: t.x + Math.cos(a) * d, y: t.y + Math.sin(a) * d };
        };
        // Éclat de l'ampoule i : une vague qui fait le tour en deux secondes, deux ou trois ampoules vives à la fois.
        const lit = (i: number) => {
          const b = Math.max(0, Math.sin(this.time * 3.2 - i * (TAU / N)));
          return 0.25 + 0.75 * b * b;
        };
        for (const l of ls) {
          if (l.ph < 100) {
            const p0 = pt(l.ph);
            const p1 = pt(l.ph + 1);
            this.seg(l, p0.x, p0.y, p1.x, p1.y, r * 0.06);
            continue;
          }
          const i = l.ph % 100;
          const q = pt(i * 2 + 1);
          const b = lit(i);
          const bulb = l.ph < 200;
          // Le culot de l'ampoule est sur le fil, le verre pointe vers l'extérieur.
          const d = r * (bulb ? 0.19 : 0.22);
          l.p.x = q.x + Math.cos(q.a) * d;
          l.p.y = q.y + Math.sin(q.a) * d;
          if (bulb) {
            l.p.rotation = q.a + Math.sin(this.time * 2 + i) * 0.2;
            l.s0 = l.s1 = scaleOf(r * 0.42);
            l.p.alpha = 0.6 + 0.4 * b;
          } else {
            l.s0 = l.s1 = scaleOf(r * (0.45 + 0.4 * b));
            l.p.alpha = 0.12 + 0.6 * b;
          }
        }
        for (let i = this.due(k, 3, dt); i > 0; i--) {
          const j = Math.floor(rnd(0, N));
          const q = pt(j * 2 + 1);
          const d = r * 0.24;
          this.spawn({ tex: 'star', x: q.x + Math.cos(q.a) * d, y: q.y + Math.sin(q.a) * d, vx: t.vx * 0.8, vy: t.vy * 0.8, life: 0.3, size: r * 0.3, size1: 0, tint: XMAS_BULBS[j % 4], spin: 4, add: true });
        }
        break;
      }
      case 'aura:boules': {
        // Cinq boules de Noël brillantes (calotte dorée, reflet blanc) en orbite lente, qui se balancent et dansent
        // doucement ; un éclat de lumière passe de temps en temps sur l'une d'elles.
        const ls = this.keep(key, id, () => {
          const out: (Live | null)[] = [];
          for (let i = 0; i < XMAS_BALLS.length; i++) {
            const orbit = (): Orbit => ({ c: t, a: (i / XMAS_BALLS.length) * TAU, r: r * 1.55, w: 0.55 });
            out.push(this.spawn({ tex: 'bauble', x: t.x, y: t.y, life: 1e9, size: r * 0.66, tint: XMAS_BALLS[i], ph: i * 1.3, orbit: orbit(), rk: 1.55 }));
            out.push(this.spawn({ tex: 'cap', x: t.x, y: t.y, life: 1e9, size: r * 0.66, tint: 0xffffff, ph: i * 1.3, orbit: orbit(), rk: 1.55 }));
          }
          return out;
        });
        this.track(ls, r);
        for (const l of ls) {
          l.oy = Math.sin(this.time * 2.1 + l.ph) * r * 0.1;
          l.p.rotation = Math.sin(this.time * 1.6 + l.ph) * 0.16;
          l.s0 = l.s1 = scaleOf(r * 0.66);
        }
        for (let i = this.due(k, 2.5, dt); i > 0 && ls.length; i--) {
          const s = pick(ls);
          this.spawn({ tex: 'star', x: s.p.x - r * 0.12, y: s.p.y - r * 0.06, vx: t.vx * 0.8, vy: t.vy * 0.8, life: 0.35, size: r * 0.28, size1: 0, tint: 0xffffff, spin: 4, add: true });
        }
        break;
      }
      case 'aura:etoilepolaire': {
        // L'étoile polaire flotte au-dessus du tank : quatre longs rayons et quatre courts qui tournent lentement avec
        // elle, une couronne de rayons fins en sens inverse, halo doré qui respire, rai de lumière qui descend sur le tank,
        // scintillement en croix, paillettes, et des flocons qui tombent. Tout est additif : rien ne masque une balle.
        // Particules permanentes posées à la main à chaque image ; l.ph donne leur rôle.
        const ls = this.keep(key, id, () => {
          const out: (Live | null)[] = [
            this.spawn({ tex: 'beam', x: t.x, y: t.y, life: 1e9, size: r, tint: 0xffe7a0, alpha: 0.12, add: true, ph: 0 }),
            this.spawn({ tex: 'dot', x: t.x, y: t.y, life: 1e9, size: r * 2.4, tint: 0xffc94a, alpha: 0.32, add: true, ph: 1 }),
          ];
          for (let i = 0; i < 12; i++) out.push(this.spawn({ tex: 'beam', x: t.x, y: t.y, life: 1e9, size: r * 0.12, tint: i < 8 ? 0xfff0c0 : 0xffd76a, alpha: 0.5, add: true, ph: 10 + i }));
          out.push(this.spawn({ tex: 'dot', x: t.x, y: t.y, life: 1e9, size: r, tint: 0xfff6dc, alpha: 0.85, add: true, ph: 2 }));
          out.push(this.spawn({ tex: 'nstar', x: t.x, y: t.y, life: 1e9, size: r * 1.05, tint: 0xffd350, add: true, ph: 3 }));
          return out;
        });
        const sx = t.x;
        const sy = t.y - r * 1.8 + Math.sin(this.time * 1.4) * r * 0.08;
        const rot = this.time * 0.3;
        const tw = Math.sin(this.time * 3.1);
        for (const l of ls) {
          const role = l.ph;
          if (role === 0) {
            // Rai vertical, de l'étoile jusqu'au centre du tank.
            l.p.x = sx;
            l.p.y = (sy + t.y) / 2;
            l.p.rotation = Math.PI / 2;
            l.lx = (t.y - sy) / 44;
            l.s0 = l.s1 = scaleOf(r * 0.9);
            l.p.alpha = 0.1 + 0.04 * Math.sin(this.time * 1.3);
            continue;
          }
          l.p.x = sx;
          l.p.y = sy;
          if (role === 1) l.s0 = l.s1 = scaleOf(r * 2.4 * (0.92 + 0.08 * Math.sin(this.time * 2)));
          else if (role === 2) l.s0 = l.s1 = scaleOf(r * (0.95 + 0.12 * tw));
          else if (role === 3) {
            l.p.rotation = rot;
            l.s0 = l.s1 = scaleOf(r * 1.05 * (1 + 0.04 * tw));
          } else {
            // Rayons : 0-7 suivent les pointes de l'étoile (pairs = longs), 8-11 forment la couronne fine inverse.
            const i = role - 10;
            const crown = i >= 8;
            const long = !crown && i % 2 === 0;
            const dir = crown ? -rot * 1.6 + ((i - 8) / 4) * TAU + Math.PI / 4 : rot - Math.PI / 2 + (i / 8) * TAU;
            const L = r * (crown ? 1.9 : long ? 2.5 : 1.4) * (0.9 + 0.1 * Math.sin(this.time * 1.7 + i));
            l.p.x = sx + Math.cos(dir) * L * 0.5;
            l.p.y = sy + Math.sin(dir) * L * 0.5;
            l.p.rotation = dir;
            l.lx = L / 44;
            l.s0 = l.s1 = scaleOf(r * (long ? 0.16 : 0.11));
            l.p.alpha = crown ? 0.18 + 0.14 * Math.sin(this.time * 3 + i) : long ? 0.55 + 0.25 * Math.sin(this.time * 2.3 + i) : 0.35 + 0.2 * Math.sin(this.time * 2.6 + i);
          }
        }
        for (let i = this.due(k, 5, dt); i > 0; i--) {
          this.spawn({ tex: 'flake', x: sx + rnd(-1.6, 1.6) * r, y: sy + rnd(-0.3, 0.5) * r, vx: t.vx * 0.7 + rnd(-6, 6), vy: t.vy * 0.7 + rnd(16, 30), sway: { a: 10, w: rnd(2, 3.5) }, life: rnd(1.6, 2.2), size: r * rnd(0.18, 0.3), tint: pick([0xffffff, 0xdff2ff, 0xfff4d6]), spin: rnd(-1.5, 1.5), fadeIn: true, alpha: 0.9 });
        }
        for (let i = this.due(`${k}:b`, 9, dt); i > 0; i--) {
          const a = rnd(0, TAU);
          const d = r * rnd(0.4, 1.3);
          this.spawn({ tex: 'star', x: sx + Math.cos(a) * d, y: sy + Math.sin(a) * d * 0.8, vx: t.vx * 0.8, vy: t.vy * 0.8 + rnd(5, 20), life: rnd(0.4, 0.7), size: r * rnd(0.18, 0.3), size1: 0, tint: pick([0xfff6d0, 0xffd76a, 0xffffff]), spin: rnd(2, 5) * sign(), add: true });
        }
        if (this.due(`${k}:c`, 0.6, dt) > 0) this.spawn({ tex: 'star', x: sx, y: sy, vx: t.vx, vy: t.vy, rot: Math.PI / 4, life: 0.45, size: r * 0.5, size1: r * 1.8, tint: 0xfff2c8, alpha: 0.7, add: true, pop: true });
        break;
      }
    }
    if (this.persist.get(key)?.id !== id) this.clearPersist(key);
  }

  /** Tank disparu : ses accumulateurs, sa traînée et ses particules d'aura permanentes sont oubliés. */
  forget(key: string): void {
    this.clearPersist(key);
    for (const s of ['', ':b', ':c']) {
      this.acc.delete(`a:${key}${s}`);
      this.acc.delete(`t:${key}${s}`);
    }
    this.lastRear.delete(`t:${key}`);
    this.ribbon.delete(`t:${key}`);
  }

  private clearPersist(key: string): void {
    const cur = this.persist.get(key);
    if (!cur) return;
    for (const l of cur.ls) l.life = 0;
    this.persist.delete(key);
  }

  /** Traînée derrière un tank qui roule. */
  trail(key: string, id: string | null, t: FxTank, dt: number): void {
    if (!id || this.budget <= 0) return;
    const speed = Math.hypot(t.vx, t.vy);
    const k = `t:${key}`;
    if (speed < 20) {
      this.lastRear.delete(k);
      this.ribbon.delete(k);
      return;
    }
    const dir = Math.atan2(t.vy, t.vx);
    const bx = t.x - Math.cos(dir) * t.r * 0.85;
    const by = t.y - Math.sin(dir) * t.r * 0.85;
    const prev = this.lastRear.get(k) ?? { x: bx, y: by };
    this.lastRear.set(k, { x: bx, y: by });
    const side = () => rnd(-0.45, 0.45) * t.r;
    const px = -Math.sin(dir);
    const py = Math.cos(dir);
    const back = (v: number) => ({ vx: -Math.cos(dir) * v, vy: -Math.sin(dir) * v });
    // Point au hasard sur le segment parcouru depuis l'image précédente, décalé sur le côté de s.
    const at = (s: number) => {
      const u = Math.random();
      return { x: prev.x + (bx - prev.x) * u + px * s, y: prev.y + (by - prev.y) * u + py * s };
    };
    const R = t.r;
    switch (id) {
      case 'trail:poussiere':
        for (let i = this.due(k, speed * 0.08, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, vx: -Math.cos(dir) * 15, vy: -Math.sin(dir) * 15, life: rnd(0.5, 0.8), size: t.r * 0.5, size1: t.r * 1.0, tint: 0xa58f73, alpha: 0.4, below: true });
        }
        break;
      case 'trail:lumiere':
        for (let i = this.due(k, 45, dt); i > 0; i--) {
          const p = at(rnd(-0.1, 0.1) * t.r);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.55, size: t.r * 0.7, size1: t.r * 0.15, tint: 0x5fe3ff, add: true, below: true });
        }
        break;
      case 'trail:bulles':
        for (let i = this.due(k, 10, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'bubble', x: p.x, y: p.y, vx: rnd(-6, 6), vy: rnd(-14, -4), life: rnd(0.8, 1.3), size: t.r * rnd(0.18, 0.35), size1: t.r * 0.45, tint: 0xbdf1ff, alpha: 0.85, below: true });
        }
        break;
      case 'trail:flammes':
        for (let i = this.due(k, 50, dt); i > 0; i--) {
          const p = at(side() * 0.7);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, vx: -Math.cos(dir) * 60 + rnd(-15, 15), vy: -Math.sin(dir) * 60 + rnd(-15, 15), life: rnd(0.2, 0.4), size: t.r * rnd(0.45, 0.7), size1: t.r * 0.1, tint: 0xfff0a0, tint2: 0xff3a10, add: true, below: true });
        }
        break;
      case 'trail:etoiles':
        for (let i = this.due(k, 14, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, life: rnd(0.6, 1.1), size: t.r * rnd(0.25, 0.45), size1: 0, tint: [0xffffff, 0xffe07a, 0xa9c8ff][Math.floor(Math.random() * 3)], spin: rnd(-3, 3), add: true, below: true });
        }
        break;
      case 'trail:arcenciel':
        for (let i = this.due(k, 55, dt); i > 0; i--) {
          const p = at(rnd(-0.12, 0.12) * t.r);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.8, size: t.r * 0.75, size1: t.r * 0.2, tint: hsl((this.time * 140 + i * 9) % 360, 0.95, 0.6), add: true, below: true });
        }
        break;
      case 'trail:givre':
        // Cristaux de glace posés au sol qui fondent, quelques reflets.
        for (let i = this.due(k, 18, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'crystal', x: p.x, y: p.y, rot: rnd(0, TAU), life: rnd(0.9, 1.4), size: R * rnd(0.3, 0.44), size1: R * 0.12, tint: pick(ICE), alpha: 0.95, hold: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 5, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, life: 0.4, size: R * 0.3, size1: 0, tint: 0xffffff, add: true, below: true });
        }
        break;
      case 'trail:lave':
        // Gouttes de lave qui refroidissent (jaune → rouge sombre) sous une lueur qui s'éteint.
        for (let i = this.due(k, 22, dt); i > 0; i--) {
          const p = at(side() * 0.8);
          this.spawn({ tex: 'orb', x: p.x, y: p.y, life: rnd(1, 1.5), size: R * rnd(0.2, 0.32), size1: R * 0.12, tint: 0xffe066, tint2: 0x4a0c04, hold: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 16, dt); i > 0; i--) {
          const p = at(side() * 0.8);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.5, size: R * 0.55, size1: R * 0.15, tint: 0xffb030, tint2: 0xff2a00, alpha: 0.8, add: true, below: true });
        }
        break;
      case 'trail:glitch':
        // Carrés décalés en rouge, vert et bleu (aberration chromatique), éclats de lignes horizontales.
        for (let i = this.due(k, 14, dt); i > 0; i--) {
          const p = at(side() * 1.3);
          const s = R * rnd(0.2, 0.36) * PIX;
          const off = R * 0.09;
          const life = rnd(0.4, 0.7);
          const blink = rnd(16, 26);
          const ph = rnd(0, TAU);
          this.spawn({ tex: 'pixel', x: p.x - off, y: p.y, life, size: s, tint: 0xff2a4a, add: true, below: true, blink, ph });
          this.spawn({ tex: 'pixel', x: p.x, y: p.y, life, size: s, tint: 0x2aff7a, add: true, below: true, blink, ph });
          this.spawn({ tex: 'pixel', x: p.x + off, y: p.y + off * 0.5, life, size: s, tint: 0x3a6bff, add: true, below: true, blink, ph });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          const p = at(side() * 1.2);
          this.spawn({ tex: 'beam', x: p.x, y: p.y, life: 0.15, size: R * 0.08, len: R * rnd(1, 1.8) * BEAM, tint: pick([0xff2a4a, 0x3dfcff, 0xffffff]), add: true, below: true });
        }
        break;
      case 'trail:feuilles':
        // Feuilles qui tombent en tournoyant et en se retournant.
        for (let i = this.due(k, 10, dt); i > 0; i--) {
          const p = at(side() * 1.2);
          this.spawn({ tex: 'leaf', x: p.x, y: p.y, ...back(20), rot: rnd(0, TAU), life: rnd(1.1, 1.6), size: R * rnd(0.4, 0.55), size1: R * 0.28, tint: pick([0x5dbb4a, 0x8bcf3f, 0x3e9e41, 0xc9b03a, 0xa7d65a]), spin: rnd(-4, 4), sway: { a: 25, w: 4 }, flap: rnd(3, 5), drag: 0.97, hold: true, below: true });
        }
        break;
      case 'trail:ecume':
        // Sillage d'écume blanche sur les deux bords, qui s'écarte, et petites bulles.
        for (let i = this.due(k, 30, dt); i > 0; i--) {
          const s = sign();
          const p = at(s * R * rnd(0.3, 0.55));
          const foam = Math.random() < 0.6;
          this.spawn({ tex: foam ? 'orb' : 'smoke', x: p.x, y: p.y, vx: px * s * 18, vy: py * s * 18, life: rnd(0.6, 0.9), size: foam ? R * rnd(0.18, 0.3) : R * 0.45, size1: foam ? R * 0.06 : R * 1.0, tint: 0xf4fbff, alpha: foam ? 0.9 : 0.55, hold: foam, drag: 0.96, below: true });
        }
        for (let i = this.due(`${k}:b`, 6, dt); i > 0; i--) {
          const p = at(side() * 1.2);
          this.spawn({ tex: 'bubble', x: p.x, y: p.y, life: 0.6, size: R * 0.1, size1: R * 0.2, tint: 0xffffff, alpha: 0.9, below: true });
        }
        break;
      case 'trail:comete':
        // Queue de comète blanche qui bleuit en refroidissant, poussière d'étoiles qui s'en échappe.
        for (let i = this.due(k, 40, dt); i > 0; i--) {
          const p = at(rnd(-0.08, 0.08) * R);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.7, size: R * 0.95, size1: R * 0.1, tint: 0xffffff, tint2: 0x3d6bff, alpha: 0.9, add: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 10, dt); i > 0; i--) {
          const p = at(side() * 1.4);
          const d = rnd(-25, 25);
          this.spawn({ tex: 'star', x: p.x, y: p.y, vx: px * d, vy: py * d, life: rnd(0.5, 0.9), size: R * rnd(0.22, 0.36), size1: 0, tint: pick([0xffffff, 0xbfd8ff, 0xfff2c0]), spin: rnd(-4, 4), add: true, below: true });
        }
        break;
      case 'trail:tapis': {
        // Ruban rouge continu bordé de deux liserés d'or : une ligne brisée, un morceau posé du dernier point au point
        // actuel dès que le tank a avancé d'un pas (au plus 17 morceaux de 3 particules par seconde).
        const step = Math.max(R * 0.35, speed / 17) / this.budget;
        const last = this.ribbon.get(k);
        if (!last) {
          this.ribbon.set(k, { x: bx, y: by });
          break;
        }
        const dx = bx - last.x;
        const dy = by - last.y;
        const d = Math.hypot(dx, dy);
        if (d > R * 8) {
          // Point périmé (traînée changée, tank téléporté) : on repart d'ici.
          this.ribbon.set(k, { x: bx, y: by });
          break;
        }
        if (d < step) break;
        this.ribbon.set(k, { x: bx, y: by });
        const rot = Math.atan2(dy, dx);
        const nx = -dy / d;
        const ny = dx / d;
        const cx = (bx + last.x) / 2;
        const cy = (by + last.y) / 2;
        const len = (d + R * 0.12) * PIX;
        this.spawn({ tex: 'pixel', x: cx, y: cy, rot, life: 1.1, size: R * 0.5 * PIX, len, tint: 0xc4162a, hold: true, below: true });
        for (const sd of [-1, 1]) this.spawn({ tex: 'pixel', x: cx + nx * sd * R * 0.27, y: cy + ny * sd * R * 0.27, rot, life: 1.1, size: R * 0.07 * PIX, len, tint: 0xffcc33, add: true, hold: true, below: true });
        break;
      }
      case 'trail:echappement':
        // Bouffées de fumée grise sombre par deux pots d'échappement, rares étincelles.
        for (let i = this.due(k, 16, dt); i > 0; i--) {
          const p = at(sign() * R * 0.35);
          const b = back(40);
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, vx: b.vx + rnd(-8, 8), vy: b.vy + rnd(-8, 8), drag: 0.95, life: rnd(0.7, 1.1), size: R * 0.35, size1: R * 1.1, tint: pick([0x6b6f77, 0x5a5e66, 0x7a7e86]), alpha: 0.85, below: true });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          const p = at(sign() * R * 0.35);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, ...back(60), life: 0.25, size: R * 0.14, size1: 0, tint: 0xff9a3d, add: true, below: true });
        }
        break;
      case 'trail:brume':
        // Brume pâle qui ondule et s'étale lentement, quelques feux pâles.
        for (let i = this.due(k, 11, dt); i > 0; i--) {
          const p = at(side() * 1.3);
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, vx: rnd(-6, 6), vy: rnd(-6, 6), sway: { a: 10, w: 1.5 }, life: rnd(1.3, 1.8), size: R * 0.7, size1: R * 1.6, tint: 0xd8e2ff, alpha: 0.26, add: true, fadeIn: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 4, dt); i > 0; i--) {
          const p = at(side() * 1.3);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, vy: -12, life: 0.9, size: R * 0.2, size1: 0, tint: 0xeef2ff, alpha: 0.6, add: true, fadeIn: true, below: true });
        }
        break;
      case 'trail:sakura':
        // Pétales de cerisier qui volettent en se retournant, reflets roses.
        for (let i = this.due(k, 16, dt); i > 0; i--) {
          const p = at(side() * 1.3);
          const b = back(25);
          const d = rnd(-20, 20);
          this.spawn({ tex: 'petal', x: p.x, y: p.y, vx: b.vx + px * d, vy: b.vy + py * d, drag: 0.97, sway: { a: 30, w: rnd(3, 5) }, rot: rnd(0, TAU), spin: rnd(-3, 3), flap: rnd(4, 7), life: rnd(1.4, 2), size: R * rnd(0.26, 0.36), size1: R * 0.18, tint: pick(SAKURA), hold: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.5, size: R * 0.22, size1: 0, tint: 0xffb8d4, add: true, below: true });
        }
        break;
      case 'trail:sable':
        // Sable soulevé : nuage clair et gerbe de grains projetés vers l'arrière.
        for (let i = this.due(k, 7, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, ...back(25), life: rnd(0.7, 1), size: R * 0.5, size1: R * 1.3, tint: 0xe8cf9a, alpha: 0.85, below: true });
        }
        for (let i = this.due(`${k}:b`, 22, dt); i > 0; i--) {
          const p = at(side() * 0.8);
          const a = dir + Math.PI + rnd(-0.6, 0.6);
          const v = rnd(50, 110);
          this.spawn({ tex: 'orb', x: p.x, y: p.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.9, life: rnd(0.4, 0.7), size: R * rnd(0.13, 0.2), tint: pick(SAND), hold: true, below: true });
        }
        break;
      case 'trail:plumes':
        // Plumes blanches qui flottent et se balancent en descendant, petits reflets.
        for (let i = this.due(k, 7, dt); i > 0; i--) {
          const p = at(side() * 1.2);
          this.spawn({ tex: 'feather', x: p.x, y: p.y, vx: rnd(-10, 10), vy: rnd(-10, 10), drag: 0.96, rot: rnd(0, TAU), spin: rnd(-1.5, 1.5), sway: { a: 22, w: rnd(2, 3) }, life: rnd(1.6, 2.2), size: R * rnd(0.38, 0.48), size1: R * 0.3, tint: pick([0xffffff, 0xfff6e2, 0xf2f2ff]), hold: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 3, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, life: 0.45, size: R * 0.24, size1: 0, tint: 0xfff8e0, add: true, below: true });
        }
        break;
      case 'trail:neige':
        // Neige fraîche soulevée : petites bouffées blanches, mottes et flocons projetés en arrière, qui fondent.
        for (let i = this.due(k, speed * 0.07, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, ...back(18), drag: 0.95, life: rnd(0.6, 0.9), size: R * 0.4, size1: R * 0.95, tint: 0xf4f9ff, alpha: 0.55, below: true });
        }
        for (let i = this.due(`${k}:b`, 12, dt); i > 0; i--) {
          const p = at(side() * 1.1);
          const a = dir + Math.PI + rnd(-0.7, 0.7);
          const v = rnd(30, 70);
          const flake = Math.random() < 0.5;
          this.spawn({ tex: flake ? 'flake' : 'chunk', x: p.x, y: p.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.9, rot: rnd(0, TAU), spin: flake ? rnd(-2, 2) : 0, life: rnd(0.8, 1.2), size: flake ? R * rnd(0.2, 0.3) : R * rnd(0.14, 0.22), size1: R * 0.06, tint: pick(SNOW), hold: true, below: true });
        }
        break;
      case 'trail:paillettes':
        // Paillettes d'or qui scintillent en tombant et en se balançant, quelques éclats plus vifs.
        for (let i = this.due(k, 26, dt); i > 0; i--) {
          const p = at(side() * 1.2);
          const star = Math.random() < 0.35;
          this.spawn({ tex: star ? 'star' : 'dot', x: p.x, y: p.y, vx: rnd(-12, 12), vy: rnd(-10, 5), grav: 60, drag: 0.97, sway: { a: 18, w: rnd(4, 7) }, rot: rnd(0, TAU), spin: star ? rnd(-4, 4) : 0, life: rnd(0.6, 1), size: star ? R * rnd(0.2, 0.3) : R * rnd(0.12, 0.2), size1: R * 0.04, tint: pick([0xffd34d, 0xffe999, 0xe8a317, 0xfff4cf]), blink: rnd(12, 22), add: true, below: true });
        }
        for (let i = this.due(`${k}:b`, 4, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, rot: rnd(0, TAU), life: 0.3, size: R * 0.42, size1: 0, tint: 0xfff2c0, add: true, below: true });
        }
        break;
      case 'trail:traineau': {
        // Deux sillons de patins de traîneau dans la neige (bourrelet blanc et creux bleuté, une ligne brisée par patin
        // comme le tapis rouge : au plus 15 morceaux de 4 particules par seconde), poussière d'or et petites étoiles.
        const step = Math.max(R * 0.4, speed / 15) / this.budget;
        const last = this.ribbon.get(k);
        const d = last ? Math.hypot(bx - last.x, by - last.y) : 0;
        if (!last || d > R * 8) this.ribbon.set(k, { x: bx, y: by });
        else if (d >= step) {
          this.ribbon.set(k, { x: bx, y: by });
          const rot = Math.atan2(by - last.y, bx - last.x);
          const nx = -(by - last.y) / d;
          const ny = (bx - last.x) / d;
          const cx = (bx + last.x) / 2;
          const cy = (by + last.y) / 2;
          const len = (d + R * 0.1) * PIX;
          for (const sd of [-1, 1]) {
            const o = R * 0.36 * sd;
            this.spawn({ tex: 'pixel', x: cx + nx * o, y: cy + ny * o, rot, life: 1.3, size: R * 0.14 * PIX, len, tint: 0xeef5ff, alpha: 0.7, hold: true, below: true });
            this.spawn({ tex: 'pixel', x: cx + nx * o, y: cy + ny * o, rot, life: 1.3, size: R * 0.06 * PIX, len, tint: 0x7f9fbd, alpha: 0.9, hold: true, below: true });
          }
        }
        for (let i = this.due(`${k}:b`, 18, dt); i > 0; i--) {
          const p = at(side() * 0.6);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, ...back(15), vy: -Math.sin(dir) * 15 - rnd(5, 20), life: rnd(0.5, 0.8), size: R * rnd(0.2, 0.3), size1: 0, tint: pick([0xffd34d, 0xffe999, 0xffc02a]), add: true, below: true });
        }
        for (let i = this.due(`${k}:c`, 4, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, rot: rnd(0, TAU), spin: rnd(-3, 3), life: rnd(0.5, 0.8), size: R * rnd(0.24, 0.32), size1: 0, tint: pick([0xffffff, 0xfff0b0]), add: true, below: true });
        }
        break;
      }
    }
  }

  // ─── Effets ponctuels ─────────────────────────────────────────────────

  /** Effet de tir à la bouche d'un tube (la balle elle-même ne change pas). */
  shot(id: string | null, x: number, y: number, angle: number, size: number): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    switch (id) {
      case 'shot:etincelles':
        for (let i = 0; i < n(9); i++) {
          const a = angle + rnd(-0.5, 0.5);
          const v = rnd(120, 260);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.12, 0.25), size: size * 0.9, size1: size * 0.2, tint: 0xffd36b, add: true, drag: 0.88 });
        }
        break;
      case 'shot:fumee':
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.35, 0.35);
          const v = rnd(30, 80);
          this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.5, 0.8), size: size * 0.6, size1: size * 1.8, tint: 0xc8ccd2, alpha: 0.4, drag: 0.94 });
        }
        break;
      case 'shot:plasma':
        this.spawn({ tex: 'ring', x, y, life: 0.22, size: size * 0.4, size1: size * 1.8, tint: 0x6fe8ff, add: true });
        this.spawn({ tex: 'dot', x, y, life: 0.15, size: size * 1.4, size1: size * 0.3, tint: 0xbff6ff, add: true });
        break;
      case 'shot:etoile':
        this.spawn({ tex: 'star', x, y, life: 0.2, size: size * 0.6, size1: size * 1.6, tint: 0xfff2b0, add: true, rot: angle });
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.8, 0.8);
          this.spawn({ tex: 'star', x, y, vx: Math.cos(a) * 140, vy: Math.sin(a) * 140, life: 0.35, size: size * 0.35, size1: 0, tint: 0xffe07a, add: true, drag: 0.9, spin: 6 });
        }
        break;
      case 'shot:cristaux':
        this.spawn({ tex: 'flake', x, y, life: 0.25, size: size * 0.5, size1: size * 1.1, tint: 0xe8f8ff, spin: 4, add: true });
        for (let i = 0; i < n(6); i++) {
          const a = angle + rnd(-0.55, 0.55);
          const v = rnd(110, 220);
          this.spawn({ tex: 'crystal', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.22, 0.35), size: size * rnd(0.6, 0.9), size1: size * 0.2, tint: pick(ICE), alpha: 0.95, drag: 0.86 });
        }
        break;
      case 'shot:eruption':
        this.spawn({ tex: 'dot', x, y, life: 0.12, size: size * 1.4, size1: size * 0.4, tint: 0xffd080, add: true });
        for (let i = 0; i < n(8); i++) {
          const a = angle + rnd(-0.45, 0.45);
          const v = rnd(90, 230);
          this.spawn({ tex: 'dot', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.25, 0.45), size: size * rnd(0.6, 1), size1: size * 0.15, tint: 0xfff0a0, tint2: 0xff2a00, add: true, drag: 0.9 });
        }
        for (let i = 0; i < n(3); i++) {
          const a = angle + rnd(-0.5, 0.5);
          const v = rnd(60, 140);
          this.spawn({ tex: 'orb', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0.45, size: size * 0.35, size1: size * 0.15, tint: 0xffb020, tint2: 0x5a1005, drag: 0.9 });
        }
        break;
      case 'shot:laser': {
        // Trait court et fin, immobile à la bouche (rien à voir avec un tir rayon qui se déplace), et un anneau.
        const L = size * 1.9;
        this.line(x, y, x + ca * L, y + sa * L, { life: 0.11, size: size * 0.4, size1: size * 0.08, tint: 0xff4fd8 });
        this.line(x, y, x + ca * L, y + sa * L, { life: 0.09, size: size * 0.14, size1: size * 0.04, tint: 0xffffff });
        this.line(x - sa * size * 0.6, y + ca * size * 0.6, x + sa * size * 0.6, y - ca * size * 0.6, { life: 0.09, size: size * 0.2, size1: 0, tint: 0xff9ae9 });
        this.spawn({ tex: 'ring', x, y, life: 0.18, size: size * 0.3, size1: size * 1.3, tint: 0xff4fd8, add: true });
        break;
      }
      case 'shot:pollen':
        this.spawn({ tex: 'smoke', x, y, life: 0.5, size: size * 0.6, size1: size * 1.6, tint: 0xfff0a0, alpha: 0.3 });
        for (let i = 0; i < n(9); i++) {
          const a = angle + rnd(-1, 1);
          const v = rnd(30, 100);
          this.spawn({ tex: 'dot', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.6, 1), size: size * rnd(0.28, 0.42), size1: size * 0.15, tint: pick([0xffe14d, 0xfff3a0, 0xf5c518]), drag: 0.92, sway: { a: 20, w: 6 }, hold: true });
        }
        break;
      case 'shot:encre':
        this.spawn({ tex: 'dot', x, y, life: 0.15, size: size * 1.2, size1: size * 0.5, tint: 0x8a4dff, alpha: 0.5, add: true });
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.4, 0.4);
          const v = rnd(40, 100);
          this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.55, 0.85), size: size * 0.7, size1: size * 2, tint: pick([0x2d1747, 0x4a2a78, 0x5e3a94]), alpha: 0.85, drag: 0.92 });
        }
        for (let i = 0; i < n(3); i++) {
          const a = angle + rnd(-0.3, 0.3);
          const v = rnd(120, 200);
          this.spawn({ tex: 'orb', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0.35, size: size * 0.25, size1: size * 0.1, tint: 0x3d1f5f, drag: 0.88 });
        }
        break;
      case 'shot:pulsar':
        this.spawn({ tex: 'dot', x, y, life: 0.12, size: size * 1.0, size1: size * 0.2, tint: 0xffffff, add: true });
        [0xf0dcff, 0xc08cff, 0x8a5cff].forEach((tint, j) => this.later(j * 0.05, () => this.spawn({ tex: 'ring', x, y, life: 0.22, size: size * 0.2, size1: size * (1.6 + j * 0.5), tint, alpha: 1 - j * 0.2, add: true })));
        break;
      case 'shot:fanfare':
        this.spawn({ tex: 'star', x, y, life: 0.15, size: size * 0.4, size1: size * 1.0, tint: 0xffe07a, add: true, rot: angle });
        for (let i = 0; i < n(10); i++) {
          const a = angle + rnd(-0.7, 0.7);
          const v = rnd(80, 190);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), life: rnd(0.45, 0.7), size: size * rnd(0.25, 0.4), tint: pick([0xffd34d, 0xfff0b0, 0xe8a317, 0xff4f5a, 0xffffff]), spin: rnd(-14, 14), flap: rnd(8, 14), drag: 0.9, grav: 160 });
        }
        break;
      case 'shot:petarade':
        this.spawn({ tex: 'flame', x: x + ca * size * 0.5, y: y + sa * size * 0.5, rot: angle + Math.PI / 2, life: 0.14, size: size * 1.4, size1: size * 0.6, tint: 0xffa33a, add: true });
        this.spawn({ tex: 'flame', x: x + ca * size * 0.3, y: y + sa * size * 0.3, rot: angle + Math.PI / 2, life: 0.08, size: size * 0.7, size1: size * 0.3, tint: 0xfff2b0, add: true });
        for (let i = 0; i < n(4); i++) {
          const a = angle + rnd(-0.5, 0.5);
          const v = rnd(25, 60);
          this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.5, 0.8), size: size * 0.5, size1: size * 1.4, tint: 0x4a4a4e, alpha: 0.5, drag: 0.93 });
        }
        for (let i = 0; i < n(2); i++) {
          const a = angle + rnd(-0.3, 0.3);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * 200, vy: Math.sin(a) * 200, rot: a, life: 0.15, size: size * 0.5, size1: 0, tint: 0xffb347, add: true, drag: 0.9 });
        }
        break;
      case 'shot:feufollet':
        this.spawn({ tex: 'flame', x: x + ca * size * 0.4, y: y + sa * size * 0.4, rot: angle + Math.PI / 2, life: 0.2, size: size * 1.3, size1: size * 0.4, tint: 0x5dff7a, add: true });
        this.spawn({ tex: 'flame', x: x + ca * size * 0.25, y: y + sa * size * 0.25, rot: angle + Math.PI / 2, life: 0.12, size: size * 0.6, size1: size * 0.2, tint: 0xd8ffd0, add: true });
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.6, 0.6);
          const v = rnd(30, 70);
          this.spawn({ tex: 'dot', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 30, life: rnd(0.45, 0.7), size: size * 0.4, size1: 0, tint: 0x7dff6a, tint2: 0x1fbf8f, add: true, sway: { a: 25, w: 8 }, drag: 0.93 });
        }
        break;
      case 'shot:eventail': {
        // Pétales et rayons ouverts en éventail régulier.
        const m = n(7);
        for (let i = 0; i < m; i++) {
          const a = m > 1 ? angle - 0.8 + (1.6 * i) / (m - 1) : angle;
          this.spawn({ tex: 'petal', x, y, vx: Math.cos(a) * 150, vy: Math.sin(a) * 150, rot: a, drag: 0.86, life: 0.4, size: size * 0.55, size1: size * 0.3, tint: i % 2 ? 0xffb3d1 : 0xff86b0 });
          if (i % 2 === 0) this.spawn({ tex: 'spark', x: x + Math.cos(a) * size * 0.8, y: y + Math.sin(a) * size * 0.8, vx: Math.cos(a) * 60, vy: Math.sin(a) * 60, rot: a, life: 0.18, size: size * 0.9, size1: 0, tint: 0xffe0ee, add: true });
        }
        break;
      }
      case 'shot:soleil': {
        this.spawn({ tex: 'dot', x, y, life: 0.14, size: size * 1.6, size1: size * 0.5, tint: 0xffe9a0, add: true });
        this.spawn({ tex: 'star', x, y, rot: rnd(0, TAU), life: 0.16, size: size * 0.8, size1: size * 1.5, tint: 0xffd04a, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.2, size: size * 0.3, size1: size * 1.4, tint: 0xffb52e, alpha: 0.8, add: true });
        const m = n(6);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU + rnd(-0.2, 0.2);
          this.spawn({ tex: 'spark', x: x + Math.cos(a) * size * 0.4, y: y + Math.sin(a) * size * 0.4, vx: Math.cos(a) * 120, vy: Math.sin(a) * 120, rot: a, life: 0.18, size: size * 0.7, size1: 0, tint: 0xffd04a, add: true, drag: 0.85 });
        }
        break;
      }
      case 'shot:rayon': {
        // Gloire de lumière blanche-dorée : halo doux et deux rayons qui s'écartent, immobiles, et des paillettes.
        const L = size * 3.4;
        this.line(x, y, x + ca * L, y + sa * L, { life: 0.22, size: size * 1.4, size1: size * 0.3, tint: 0xffe7a3, alpha: 0.7 });
        for (const d of [-0.16, 0.16]) this.line(x, y, x + Math.cos(angle + d) * L * 1.1, y + Math.sin(angle + d) * L * 1.1, { life: 0.18, size: size * 0.3, size1: size * 0.05, tint: 0xffffff, alpha: 0.85 });
        this.spawn({ tex: 'star', x, y, rot: angle, life: 0.18, size: size * 0.7, size1: size * 1.3, tint: 0xfff6d8, add: true });
        for (let i = 0; i < n(5); i++) {
          const u = rnd(0.2, 1) * L;
          const d = rnd(-20, 20);
          this.spawn({ tex: 'star', x: x + ca * u, y: y + sa * u, vx: -sa * d, vy: ca * d, life: rnd(0.25, 0.45), size: size * rnd(0.25, 0.4), size1: 0, tint: 0xffd76a, spin: 5, add: true });
        }
        break;
      }
      case 'shot:clochettes': {
        // Une ou deux clochettes dorées jaillissent de la bouche en se balançant, un anneau doré et quelques reflets.
        this.spawn({ tex: 'ring', x, y, life: 0.2, size: size * 0.3, size1: size * 1.3, tint: 0xffd34d, alpha: 0.8, add: true, pop: true });
        const m = Math.min(2, n(2));
        for (let i = 0; i < m; i++) {
          const a = angle + (m === 1 ? 0 : i ? 0.55 : -0.55);
          const v = rnd(70, 110);
          this.spawn({
            tex: 'bell',
            x,
            y,
            vx: Math.cos(a) * v,
            vy: Math.sin(a) * v,
            drag: 0.88,
            life: 0.42,
            size: size * 0.35,
            size1: size * 0.85,
            tint: 0xffffff,
            pop: true,
            hold: true,
            // Balancement qui s'amortit.
            emit: (l) => (l.p.rotation = Math.sin(l.ph + (l.max - l.life) * 22) * 0.5 * (l.life / l.max)),
          });
        }
        for (let i = 0; i < n(4); i++) {
          const a = angle + rnd(-0.9, 0.9);
          const v = rnd(60, 130);
          this.spawn({ tex: 'star', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.88, life: rnd(0.2, 0.32), size: size * 0.3, size1: 0, tint: pick([0xfff2b0, 0xffd34d]), spin: 6, add: true });
        }
        break;
      }
      case 'shot:papillotes':
        // « Bang » de papillote : éclair blanc, petite bouffée, confettis carrés et serpentins de papier de toutes les couleurs.
        this.spawn({ tex: 'dot', x, y, life: 0.12, size: size * 1.3, size1: size * 0.4, tint: 0xfff6e0, add: true });
        this.spawn({ tex: 'star', x, y, rot: rnd(0, TAU), life: 0.12, size: size * 0.5, size1: size * 1.3, tint: 0xffffff, add: true });
        this.spawn({ tex: 'smoke', x, y, vx: ca * 30, vy: sa * 30, life: 0.4, size: size * 0.5, size1: size * 1.3, tint: 0xf2eee8, alpha: 0.35, drag: 0.93 });
        for (let i = 0; i < n(8); i++) {
          const a = angle + rnd(-0.9, 0.9);
          const v = rnd(90, 200);
          this.spawn({ tex: 'pixel', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), spin: rnd(-12, 12), flap: rnd(8, 14), drag: 0.88, grav: 120, life: rnd(0.4, 0.65), size: size * rnd(0.16, 0.24) * PIX, tint: pick(XMAS_PAPER), hold: true });
        }
        for (let i = 0; i < n(4); i++) {
          const a = angle + rnd(-0.6, 0.6);
          const v = rnd(70, 150);
          this.spawn({ tex: 'strip', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-8, 8), flap: rnd(5, 9), drag: 0.9, grav: 90, life: rnd(0.5, 0.7), size: size * rnd(0.45, 0.6), tint: pick(XMAS_PAPER), hold: true });
        }
        break;
      case 'shot:etoilefilante': {
        // Petite étoile filante qui part en biais de la bouche avec une traînée lumineuse et une queue de poussière
        // d'étoiles ; elle s'arrête en un instant près du tube (la balle elle-même ne change pas).
        const a = angle + sign() * rnd(0.3, 0.45);
        const c = Math.cos(a);
        const s = Math.sin(a);
        const o = { vx: c * 280, vy: s * 280, drag: 0.9, life: 0.3, add: true };
        this.spawn({ tex: 'dot', x, y, life: 0.1, size: size * 1.1, size1: size * 0.3, tint: 0xfff2c0, add: true });
        this.spawn({ tex: 'spark', x: x - c * size * 0.5, y: y - s * size * 0.5, ...o, face: 0, size: size * 1.2, size1: size * 0.3, tint: 0xffe39a, alpha: 0.85 });
        this.spawn({
          tex: 'star',
          x,
          y,
          ...o,
          spin: 9,
          size: size * 0.6,
          size1: size * 0.3,
          tint: 0xfff8e0,
          emit: this.emitter(36, (l) =>
            this.spawn({ tex: Math.random() < 0.4 ? 'star' : 'dot', x: l.p.x + rnd(-2, 2), y: l.p.y + rnd(-2, 2), vx: -c * 20 + rnd(-15, 15), vy: -s * 20 + rnd(-15, 15), life: rnd(0.2, 0.35), size: size * rnd(0.2, 0.32), size1: 0, tint: pick([0xfff2b0, 0xffffff, 0xbfd8ff, 0xffd76a]), spin: 6, add: true }),
          ),
        });
        break;
      }
    }
  }

  /** Effet d'élimination, à la position de la victime. */
  kill(id: string | null, x: number, y: number, r: number): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    switch (id) {
      case 'kill:confettis':
        for (let i = 0; i < n(45); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(60, 160);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 80, life: rnd(1, 1.6), size: r * rnd(0.25, 0.4), tint: hsl(rnd(0, 360), 0.9, 0.6), spin: rnd(-10, 10), drag: 0.95, grav: 260 });
        }
        break;
      case 'kill:onde':
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r, size1: r * 3, tint: 0x9fd8ff, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.6, size: r * 0.5, size1: r * 2.2, tint: 0xffffff, alpha: 0.6, add: true });
        this.spawn({ tex: 'dot', x, y, life: 0.25, size: r * 1.8, size1: r * 0.6, tint: 0xd8f0ff, add: true });
        break;
      case 'kill:foudre':
        for (let i = 0; i < 3; i++) this.bolt(x + rnd(-r, r), y - r * 4, x + rnd(-r * 0.3, r * 0.3), y, 0x8fd0ff, 2, 0.25);
        this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 2, size1: r * 0.6, tint: 0xbfe6ff, add: true });
        for (let i = 0; i < n(20); i++) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * rnd(70, 160), vy: Math.sin(a) * rnd(70, 160), rot: a, life: 0.35, size: r * 0.7, size1: 0, tint: 0xbfe6ff, add: true, drag: 0.9 });
        }
        break;
      case 'kill:supernova':
        this.spawn({ tex: 'dot', x, y, life: 0.45, size: r * 2.6, size1: r * 0.8, tint: 0xfff2d0, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.7, size: r, size1: r * 4, tint: 0xff9a4a, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.55, size: r, size1: r * 3, tint: 0xff4f7b, add: true });
        for (let i = 0; i < n(60); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(90, 220);
          this.spawn({ tex: i % 3 ? 'spark' : 'star', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.5, 1), size: r * 0.8, size1: 0, tint: [0xfff2b0, 0xff9a4a, 0xff4f7b][i % 3], add: true, drag: 0.93 });
        }
        break;
      case 'kill:gel': {
        // La victime se couvre de glace (boule givrée, cristaux qui poussent), puis tout éclate.
        this.spawn({ tex: 'orb', x, y, life: 0.5, size: r * 1.2, size1: r * 1.9, tint: 0xd8f3ff, alpha: 0.75, pop: true, hold: true });
        this.spawn({ tex: 'dot', x, y, life: 0.5, size: r * 2.2, size1: r * 1.6, tint: 0xcff4ff, alpha: 0.6, add: true, fadeIn: true });
        const m = n(10);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU + rnd(-0.2, 0.2);
          this.spawn({ tex: 'crystal', x: x + Math.cos(a) * r * 0.6, y: y + Math.sin(a) * r * 0.6, rot: a, life: 0.5, size: r * 0.15, size1: r * 0.75, tint: pick([0xffffff, 0xcdeeff]), hold: true, pop: true });
        }
        this.later(0.45, () => {
          this.spawn({ tex: 'ring', x, y, life: 0.4, size: r, size1: r * 3.2, tint: 0xe8f8ff, add: true, pop: true });
          this.spawn({ tex: 'dot', x, y, life: 0.2, size: r * 2, size1: r * 0.5, tint: 0xffffff, add: true });
          for (let i = 0; i < n(28); i++) {
            const a = rnd(0, TAU);
            const v = rnd(100, 240);
            this.spawn({ tex: i % 3 ? 'shard' : 'crystal', x: x + Math.cos(a) * r * 0.4, y: y + Math.sin(a) * r * 0.4, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-10, 10), drag: 0.9, life: rnd(0.6, 1), size: r * rnd(0.25, 0.5), tint: pick([0xffffff, 0xd4f1ff, 0xa8dcff, 0x7fc8ff]), hold: true });
          }
        });
        break;
      }
      case 'kill:meteores': {
        // Trois ou quatre météores tombent en diagonale avec une traînée de feu, chacun s'écrase près de la victime.
        const m = Math.min(4, n(4));
        const th = Math.PI / 4 + rnd(-0.15, 0.15);
        const c = Math.cos(th);
        const s = Math.sin(th);
        for (let i = 0; i < m; i++) {
          this.later(i * 0.13, () => {
            const tx = x + rnd(-1.1, 1.1) * r;
            const ty = y + rnd(-1.1, 1.1) * r;
            const L = r * 5;
            const life = 0.32;
            const v = L / life;
            const o = { x: tx - c * L, y: ty - s * L, vx: c * v, vy: s * v, life };
            this.spawn({ tex: 'spark', ...o, rot: th, size: r * 1.6, size1: r * 1.4, tint: 0xffe0a0, add: true, hold: true });
            this.spawn({
              tex: 'dot',
              ...o,
              size: r * 0.75,
              tint: 0xfff4d0,
              add: true,
              hold: true,
              emit: this.emitter(22, (l) => this.spawn({ tex: 'dot', x: l.p.x + rnd(-2, 2), y: l.p.y + rnd(-2, 2), vx: -c * 40, vy: -s * 40, life: rnd(0.25, 0.4), size: r * rnd(0.45, 0.65), size1: r * 0.1, tint: 0xffd060, tint2: 0xb01a00, add: true })),
              onEnd: (l) => {
                const ix = l.p.x;
                const iy = l.p.y;
                this.spawn({ tex: 'ring', x: ix, y: iy, life: 0.35, size: r * 0.4, size1: r * 2, tint: 0xff8a3d, add: true, pop: true });
                this.spawn({ tex: 'dot', x: ix, y: iy, life: 0.2, size: r * 1.5, size1: r * 0.3, tint: 0xffd9a0, add: true });
                this.spawn({ tex: 'smoke', x: ix, y: iy, vy: -15, life: 0.8, size: r * 0.6, size1: r * 1.6, tint: 0x3a2a22, alpha: 0.45 });
                for (let j = 0; j < n(3); j++) {
                  const a = rnd(0, TAU);
                  const sv = rnd(90, 170);
                  this.spawn({ tex: 'spark', x: ix, y: iy, vx: Math.cos(a) * sv, vy: Math.sin(a) * sv, rot: a, life: 0.3, size: r * 0.6, size1: 0, tint: 0xffb040, add: true, drag: 0.9 });
                }
              },
            });
          });
        }
        break;
      }
      case 'kill:pixelisation': {
        // La cible se dissout en pixels carrés alignés sur une grille, qui clignotent et s'envolent un à un.
        const step = r * 0.3;
        for (let gx = -r; gx <= r; gx += step)
          for (let gy = -r; gy <= r; gy += step) {
            if (gx * gx + gy * gy > r * r || Math.random() > this.budget) continue;
            this.spawn({ tex: 'pixel', x: x + gx, y: y + gy, vx: rnd(-10, 25), vy: -rnd(20, 60), life: rnd(0.25, 1), size: step * 0.92 * PIX, size1: step * 0.5 * PIX, tint: Math.random() < 0.08 ? 0xff3df0 : pick([0x39ff8f, 0x3dfcff, 0xffffff, 0x2ee6a0]), hold: true, blink: rnd(10, 20) });
          }
        this.spawn({ tex: 'dot', x, y, life: 0.2, size: r * 1.8, size1: r * 0.6, tint: 0x3dfcff, add: true });
        for (let j = 0; j < 2; j++) {
          const yy = y + rnd(-r, r) * 0.8;
          this.line(x - r * 1.4, yy, x + r * 1.4, yy, { life: 0.2, size: r * 0.12, tint: pick([0x3dfcff, 0xff3df0]), blink: 30 });
        }
        break;
      }
      case 'kill:floraison': {
        // Des fleurs éclosent une à une autour de la victime, une gerbe de pétales et quelques feuilles.
        const fc = [0xff7ab6, 0xffd23f, 0xffffff, 0xff8c42, 0xc77dff, 0xff5c7a];
        const m = n(7);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU + rnd(-0.3, 0.3);
          const d = i === 0 ? 0 : r * rnd(0.6, 1.4);
          const fx = x + Math.cos(a) * d;
          const fy = y + Math.sin(a) * d;
          this.later(i * 0.05, () => {
            const life = rnd(1, 1.4);
            const big = r * rnd(0.6, 0.85);
            this.spawn({ tex: 'flower', x: fx, y: fy, rot: rnd(0, TAU), spin: rnd(-1, 1), life, size: r * 0.1, size1: big, tint: pick(fc), pop: true, hold: true });
            this.spawn({ tex: 'orb', x: fx, y: fy, life, size: r * 0.04, size1: big * 0.28, tint: 0xffe14d, pop: true, hold: true });
          });
        }
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r * 0.5, size1: r * 2.6, tint: 0x8cff7a, alpha: 0.6, add: true, pop: true });
        for (let i = 0; i < n(20); i++) {
          const a = rnd(0, TAU);
          const v = rnd(60, 140);
          this.spawn({ tex: 'petal', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-5, 5), sway: { a: 25, w: 5 }, drag: 0.93, life: rnd(0.8, 1.2), size: r * rnd(0.25, 0.35), size1: r * 0.15, tint: pick(fc) });
        }
        for (let i = 0; i < n(5); i++) {
          const a = rnd(0, TAU);
          const v = rnd(50, 110);
          this.spawn({ tex: 'leaf', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-4, 4), drag: 0.93, life: rnd(0.8, 1.1), size: r * 0.35, size1: r * 0.2, tint: pick([0x5dbb4a, 0x8bcf3f]) });
        }
        break;
      }
      case 'kill:tourbillon': {
        // Spirale d'eau à trois bras qui tourne en s'élargissant.
        const c = { x, y };
        const per = n(12);
        for (let arm = 0; arm < 3; arm++)
          for (let j = 0; j < per; j++) {
            const bub = j % 4 === 0;
            this.spawn({ tex: bub ? 'bubble' : 'spark', x, y, life: 0.75 + j * 0.02, size: bub ? r * 0.24 : r * (0.45 + j * 0.04), size1: r * 0.15, tint: bub ? 0xe6f8ff : pick([0x5fd0ff, 0xbff0ff, 0x2a9df4, 0xffffff]), add: !bub, fadeIn: true, face: bub ? undefined : 0, orbit: { c, a: (arm * TAU) / 3 + j * 0.32, r: r * (0.15 + j * 0.09), w: 7 - j * 0.25, dr: r * 1.4 } });
          }
        this.spawn({ tex: 'ring', x, y, life: 0.55, size: r * 0.4, size1: r * 3, tint: 0x7fd8ff, alpha: 0.45, add: true, pop: true });
        this.spawn({ tex: 'dot', x, y, life: 0.2, size: r * 1.6, size1: r * 0.4, tint: 0xbff0ff, add: true });
        break;
      }
      case 'kill:trounoir': {
        // Disque noir cerclé de violet qui aspire des traits lumineux en spirale, puis éclate.
        const c = { x, y };
        this.spawn({ tex: 'smoke', x, y, life: 0.9, size: r * 2.6, tint: 0x000000, alpha: 0.7, hold: true });
        this.spawn({ tex: 'orb', x, y, life: 0.9, size: r * 0.3, size1: r * 1.4, tint: 0x05020a, alpha: 0.95, pop: true, hold: true });
        this.spawn({ tex: 'ring', x, y, life: 0.9, size: r * 2.2, size1: r * 1.3, tint: 0xb36bff, alpha: 0.9, add: true, hold: true });
        this.spawn({ tex: 'ring', x, y, life: 0.9, size: r * 2.8, size1: r * 1.1, tint: 0xff7ad9, alpha: 0.45, add: true, hold: true });
        for (let i = 0; i < n(28); i++) {
          const r0 = r * rnd(1.6, 3);
          const life = rnd(0.55, 0.85);
          this.spawn({ tex: 'spark', x, y, life, size: r * 0.55, size1: r * 0.15, tint: pick([0xd9a8ff, 0xff9ae6, 0xffffff, 0x9a8cff]), add: true, fadeIn: true, face: 0, orbit: { c, a: rnd(0, TAU), r: r0, w: rnd(3, 5), dr: -(r0 - r * 0.35) / life } });
        }
        this.later(0.85, () => {
          this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 2.6, size1: r * 0.6, tint: 0xf0dcff, add: true });
          this.spawn({ tex: 'ring', x, y, life: 0.5, size: r, size1: r * 4.2, tint: 0xd9a8ff, add: true, pop: true });
          this.spawn({ tex: 'ring', x, y, life: 0.4, size: r * 0.5, size1: r * 3, tint: 0xffffff, alpha: 0.6, add: true, pop: true });
          for (let i = 0; i < n(22); i++) {
            const a = rnd(0, TAU);
            const v = rnd(160, 300);
            this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.35, 0.6), size: r * 0.8, size1: 0, tint: pick([0xd9a8ff, 0xff9ae6, 0xffffff]), add: true, drag: 0.9 });
          }
        });
        break;
      }
      case 'kill:pluiedor': {
        // Fontaine puis pluie de pièces d'or qui tournent et rebondissent au sol, éclats dorés.
        this.spawn({ tex: 'dot', x, y, life: 0.25, size: r * 2, size1: r * 0.5, tint: 0xffe39a, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r * 0.5, size1: r * 3, tint: 0xffcc33, add: true, pop: true });
        const coin = (cx: number, cy: number, vx: number, vy: number, floor: number, life: number) =>
          this.spawn({ tex: 'coin', x: cx, y: cy, vx, vy, grav: 750, floor, bounce: 0.45, life, hold: true, flap: rnd(8, 14), size: r * rnd(0.32, 0.42), tint: pick(GOLD) });
        for (let i = 0; i < n(20); i++) coin(x + rnd(-0.3, 0.3) * r, y, rnd(-130, 130), -rnd(220, 360), y + r * rnd(0.3, 1.1), rnd(1.1, 1.5));
        this.later(0.15, () => {
          for (let i = 0; i < n(12); i++) coin(x + rnd(-1.6, 1.6) * r, y - r * rnd(2.5, 4), rnd(-20, 20), rnd(50, 150), y + r * rnd(-0.2, 1.2), rnd(1, 1.3));
        });
        for (let i = 0; i < n(12); i++) {
          const gx = x + rnd(-2, 2) * r;
          const gy = y + rnd(-1.8, 1) * r;
          this.later(rnd(0, 0.7), () => this.spawn({ tex: 'star', x: gx, y: gy, life: rnd(0.3, 0.5), size: r * 0.4, size1: 0, tint: 0xfff3c0, spin: 4, add: true }));
        }
        break;
      }
      case 'kill:boulons':
        this.spawn({ tex: 'dot', x, y, life: 0.15, size: r * 1.4, size1: r * 0.4, tint: 0xffd0a0, add: true });
        for (let i = 0; i < n(16); i++) {
          const a = rnd(0, TAU);
          const v = rnd(110, 230);
          this.spawn({ tex: i % 2 ? 'nut' : 'screw', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 90, rot: rnd(0, TAU), grav: 420, floor: y + r * rnd(0.4, 1.2), bounce: 0.4, spin: rnd(-14, 14), drag: 0.97, life: rnd(0.9, 1.3), hold: true, size: r * rnd(0.3, 0.42), tint: pick([0xc3cbd3, 0x9aa3ad, 0xd9a55b, 0x7d8690]) });
        }
        for (let i = 0; i < n(6); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * 180, vy: Math.sin(a) * 180, rot: a, life: 0.25, size: r * 0.6, size1: 0, tint: 0xffa040, add: true, drag: 0.9 });
        }
        for (let i = 0; i < n(3); i++) this.spawn({ tex: 'smoke', x: x + rnd(-r, r) * 0.4, y: y + rnd(-r, r) * 0.4, vy: -15, life: rnd(0.6, 0.9), size: r * 0.6, size1: r * 1.4, tint: 0x45474c, alpha: 0.5 });
        break;
      case 'kill:chauvesouris': {
        // Une nuée de chauves-souris s'envole en trois vagues, ailes qui battent, depuis un nuage violet sombre.
        this.spawn({ tex: 'ring', x, y, life: 0.4, size: r * 0.5, size1: r * 2.5, tint: 0x9b5cff, alpha: 0.5, add: true, pop: true });
        for (let i = 0; i < n(4); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * 25, vy: Math.sin(a) * 25, life: 0.8, size: r * 0.8, size1: r * 2, tint: 0x2a1838, alpha: 0.55, drag: 0.95 });
        }
        for (const w of [0, 0.12, 0.24])
          this.later(w, () => {
            for (let i = 0; i < n(6); i++) {
              const a = -Math.PI / 2 + rnd(-1.4, 1.4);
              const v = rnd(70, 130);
              const o = { x: x + rnd(-0.5, 0.5) * r, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 1.015, sway: { a: 60, w: rnd(6, 9) }, ph: rnd(0, TAU), life: rnd(1, 1.4), hold: true };
              this.spawn({ tex: 'smoke', ...o, size: r * 0.9, size1: r * 0.6, tint: 0x7a3cff, alpha: 0.3, add: true });
              this.spawn({ tex: 'bat', ...o, flap: rnd(18, 26), face: Math.PI / 2, size: r * rnd(0.45, 0.6), size1: r * 0.32, tint: pick([0x5b4a7a, 0x6a5590, 0x4b3a66]) });
            }
          });
        break;
      }
      case 'kill:hanabi': {
        // Trois salves de feu d'artifice multicolores, gerbes sphériques qui retombent, paillettes.
        for (let s = 0; s < 3; s++)
          this.later(s * 0.3, () => {
            const cx = s === 0 ? x : x + rnd(-1.4, 1.4) * r;
            const cy = s === 0 ? y : y - r * rnd(0.3, 1.6);
            const h = rnd(0, 360);
            const c1 = hsl(h, 1, 0.65);
            const c2 = hsl((h + 30) % 360, 1, 0.38);
            this.spawn({ tex: 'dot', x: cx, y: cy, life: 0.18, size: r * 1.6, size1: r * 0.4, tint: 0xffffff, add: true });
            const m = n(16);
            for (let i = 0; i < m; i++) {
              const a = (i / m) * TAU + rnd(-0.08, 0.08);
              const v = r * rnd(6.5, 7.5);
              this.spawn({ tex: 'spark', x: cx, y: cy, vx: Math.cos(a) * v, vy: Math.sin(a) * v, face: 0, drag: 0.92, grav: 70, life: rnd(0.8, 1.05), size: r * 0.75, size1: r * 0.15, tint: c1, tint2: c2, add: true });
            }
            for (let j = 0; j < n(3); j++) this.spawn({ tex: 'star', x: cx + rnd(-1.5, 1.5) * r, y: cy + rnd(-1.5, 1.5) * r, life: rnd(0.6, 0.9), size: r * 0.3, tint: 0xfff6d0, add: true, fadeIn: true, blink: 20 });
          });
        break;
      }
      case 'kill:tornade': {
        // Entonnoir de sable : des ellipses de traits empilées, plus larges en haut, qui tournent vite et montent.
        const c = { x, y: y + r * 0.9 };
        const levels = 7;
        const per = n(4);
        for (let j = 0; j < levels; j++) {
          const h = j / (levels - 1);
          const a0 = rnd(0, TAU);
          for (let i = 0; i < per; i++)
            this.spawn({ tex: 'spark', x, y, oy: -h * r * 3, vy: -r * 0.8, life: rnd(1, 1.3), size: r * (0.5 + h * 0.5), size1: r * 0.3, tint: pick(SAND), fadeIn: true, face: 0, orbit: { c, a: a0 + (i / per) * TAU, r: r * (0.2 + h * 1.1), w: 9 - h * 3, dr: r * 0.25, sq: 0.3 } });
        }
        for (let i = 0; i < n(6); i++) {
          const h = rnd(0, 1);
          this.spawn({ tex: 'orb', x, y, oy: -h * r * 3, vy: -r * 0.8, life: rnd(0.9, 1.2), size: r * rnd(0.12, 0.18), tint: pick(SAND), fadeIn: true, orbit: { c, a: rnd(0, TAU), r: r * (0.2 + h * 1.1), w: 8, sq: 0.3 } });
        }
        for (let i = 0; i < n(5); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'smoke', x: c.x + Math.cos(a) * r * 0.6, y: c.y + Math.sin(a) * r * 0.25, vx: Math.cos(a) * 30, vy: Math.sin(a) * 10, life: 1, size: r * 0.8, size1: r * 1.8, tint: 0xd9b77a, alpha: 0.35, drag: 0.96 });
        }
        break;
      }
      case 'kill:jugement': {
        // Une colonne de lumière tombe du ciel sur la victime, puis onde dorée et étincelles.
        const H = r * 7;
        this.spawn({ tex: 'dot', x, y: y - H, vy: H / 0.16, life: 0.16, size: r * 0.9, tint: 0xfff6d8, add: true, hold: true });
        this.line(x, y - H, x, y, { life: 0.16, size: r * 0.25, tint: 0xfff6d8, alpha: 0.8, fadeIn: true });
        this.later(0.16, () => {
          this.line(x, y - H, x, y + r * 0.3, { life: 0.55, size: r * 2.4, size1: r * 0.3, tint: 0xffe7a0, alpha: 0.9 });
          this.line(x, y - H, x, y + r * 0.3, { life: 0.45, size: r * 0.9, size1: r * 0.1, tint: 0xffffff });
          this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 2.8, size1: r * 0.8, tint: 0xfff2c0, add: true });
          this.spawn({ tex: 'ring', x, y, life: 0.6, size: r * 0.6, size1: r * 4.4, tint: 0xffc93c, add: true, pop: true });
          this.later(0.1, () => this.spawn({ tex: 'ring', x, y, life: 0.5, size: r * 0.4, size1: r * 3.2, tint: 0xfff0b0, alpha: 0.7, add: true, pop: true }));
          for (let i = 0; i < n(18); i++) {
            const a = rnd(0, TAU);
            const v = rnd(150, 260);
            this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: 0.4, size: r * 0.7, size1: 0, tint: pick([0xffd76a, 0xfff0b0, 0xffb52e]), add: true, drag: 0.9 });
          }
          for (let i = 0; i < n(8); i++) this.spawn({ tex: 'star', x: x + rnd(-0.5, 0.5) * r, y: y - rnd(0, H * 0.6), vy: -rnd(40, 90), life: rnd(0.5, 0.9), size: r * 0.3, size1: 0, tint: 0xfff0b0, add: true, fadeIn: true });
        });
        break;
      }
      case 'kill:cadeau': {
        // Un paquet cadeau surgit sur la victime et tremble, puis son couvercle saute en tournoyant : confettis, serpentins
        // et étincelles jaillissent. Paquet et couvercle sont au sol (sous les balles et les tanks).
        const c = pick([
          { box: 0xd8202e, rib: 0xffd34d },
          { box: 0x1f9e4a, rib: 0xff3b4f },
          { box: 0x2f6fe0, rib: 0xf4f4f4 },
          { box: 0xf2b630, rib: 0xd8202e },
        ]);
        const S = r * 1.3;
        const s0 = scaleOf(S);
        const OPEN = 0.45;
        const lidDy = -14.5 * s0;
        // Animation commune aux quatre morceaux (ils restent alignés) : apparition qui rebondit, puis tremblement.
        const shape = (dy: number, solid: boolean) => (l: Live) => {
          const age = l.max - l.life;
          const g = age < 0.16 ? backOut(age / 0.16) : 1;
          const shake = age > 0.16 && age < OPEN ? Math.sin(age * 60) * r * 0.05 * ((age - 0.16) / (OPEN - 0.16)) : 0;
          l.p.x = x + shake;
          l.p.y = y + dy * g;
          l.p.scaleX = l.p.scaleY = s0 * g;
          if (solid) l.p.alpha = 1;
        };
        const fvx = rnd(-1, 1) * r * 1.6;
        const fspin = rnd(5, 9) * sign();
        const fly = (tex: Tex, tint: number) => (l: Live) => this.spawn({ tex, x: l.p.x, y: l.p.y, vx: fvx, vy: -r * 7, grav: r * 18, spin: fspin, life: 0.8, size: S, tint, hold: true, below: true });
        this.spawn({ tex: 'gift', x, y, life: 1.2, size: S * 0.05, tint: c.box, hold: true, below: true, emit: shape(0, false) });
        this.spawn({ tex: 'giftrib', x, y, life: 1.2, size: S * 0.05, tint: c.rib, hold: true, below: true, emit: shape(0, false) });
        this.spawn({ tex: 'lid', x, y: y + lidDy, life: OPEN, size: S * 0.05, tint: c.box, below: true, emit: shape(lidDy, true), onEnd: fly('lid', c.box) });
        this.spawn({ tex: 'bow', x, y: y + lidDy, life: OPEN, size: S * 0.05, tint: c.rib, below: true, emit: shape(lidDy, true), onEnd: fly('bow', c.rib) });
        this.later(OPEN, () => {
          const oy = y + lidDy;
          this.spawn({ tex: 'dot', x, y: oy, life: 0.25, size: r * 1.6, size1: r * 0.4, tint: 0xfff4d0, add: true });
          this.spawn({ tex: 'ring', x, y: oy, life: 0.4, size: r * 0.4, size1: r * 2.6, tint: c.rib, alpha: 0.7, add: true, pop: true });
          for (let i = 0; i < n(24); i++) {
            const a = -Math.PI / 2 + rnd(-1.3, 1.3);
            const v = rnd(120, 260);
            const sq = i % 3 > 0;
            this.spawn({ tex: sq ? 'pixel' : 'shard', x, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), spin: rnd(-12, 12), flap: rnd(6, 12), drag: 0.95, grav: 300, life: rnd(0.9, 1.3), size: sq ? r * rnd(0.14, 0.2) * PIX : r * rnd(0.22, 0.32), tint: pick(XMAS_PAPER), hold: true });
          }
          for (let i = 0; i < n(7); i++) {
            const a = -Math.PI / 2 + rnd(-1, 1);
            const v = rnd(110, 200);
            this.spawn({ tex: 'strip', x, y: oy, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-6, 6), flap: rnd(3, 6), sway: { a: 30, w: 4 }, drag: 0.94, grav: 220, life: rnd(1, 1.3), size: r * rnd(0.55, 0.75), tint: pick([c.rib, c.box, 0xffd34d]), hold: true });
          }
          for (let i = 0; i < n(6); i++) {
            const gx = x + rnd(-1.4, 1.4) * r;
            const gy = oy + rnd(-1.6, 0.4) * r;
            this.later(rnd(0, 0.4), () => this.spawn({ tex: 'star', x: gx, y: gy, life: rnd(0.3, 0.5), size: r * 0.35, size1: 0, tint: 0xfff3c0, spin: 4, add: true }));
          }
        });
        break;
      }
      case 'kill:nouvelan': {
        // Feux du Nouvel An : trois fusées montent en sifflant depuis la victime et éclatent l'une après l'autre en or,
        // argent puis rouge ; chaque gerbe laisse une traîne scintillante et finit en crépitements blancs. (Hanabi : gerbes
        // multicolores sur place, sans fusée ni crépitement.)
        const shells = [
          { c1: 0xfff0a0, c2: 0xe89a10 },
          { c1: 0xffffff, c2: 0x9aa8c0 },
          { c1: 0xff4058, c2: 0x8a0a1e },
        ];
        const crackle = (cx: number, cy: number) =>
          this.later(rnd(0, 0.18), () => {
            this.spawn({ tex: 'star', x: cx + rnd(-3, 3), y: cy + rnd(-3, 3), rot: rnd(0, TAU), life: rnd(0.12, 0.2), size: r * 0.45, size1: r * 0.1, tint: 0xffffff, add: true });
            this.spawn({ tex: 'dot', x: cx, y: cy, life: 0.1, size: r * 0.5, size1: 0, tint: 0xfff6e0, add: true });
          });
        const burst = (cx: number, cy: number, c1: number, c2: number) => {
          this.spawn({ tex: 'dot', x: cx, y: cy, life: 0.2, size: r * 1.8, size1: r * 0.5, tint: 0xffffff, add: true });
          this.spawn({ tex: 'ring', x: cx, y: cy, life: 0.3, size: r * 0.3, size1: r * 2.2, tint: c1, alpha: 0.5, add: true, pop: true });
          const m = n(12);
          for (let i = 0; i < m; i++) {
            const a = (i / m) * TAU + rnd(-0.1, 0.1);
            const v = r * rnd(5, 6.2);
            this.spawn({
              tex: 'dot',
              x: cx,
              y: cy,
              vx: Math.cos(a) * v,
              vy: Math.sin(a) * v,
              drag: 0.9,
              grav: 55,
              life: rnd(0.75, 0.95),
              size: r * 0.38,
              size1: r * 0.12,
              tint: c1,
              tint2: c2,
              add: true,
              emit: this.emitter(8, (l) => this.spawn({ tex: 'dot', x: l.p.x, y: l.p.y, vy: 12, life: 0.28, size: r * 0.16, size1: 0, tint: c2, add: true })),
              onEnd: (l) => {
                if (Math.random() < 0.55) crackle(l.p.x, l.p.y);
              },
            });
          }
        };
        this.spawn({ tex: 'dot', x, y, life: 0.15, size: r * 1.2, size1: r * 0.3, tint: 0xffe0a0, add: true });
        shells.forEach((sh, s) =>
          this.later(s * 0.22, () => {
            const T = 0.28;
            const x0 = x + rnd(-0.3, 0.3) * r;
            const tx = x + (s - 1) * r * 1.3 + rnd(-0.3, 0.3) * r;
            const ty = y - r * rnd(2.2, 3.2);
            this.spawn({
              tex: 'dot',
              x: x0,
              y,
              vx: (tx - x0) / T,
              vy: (ty - y) / T,
              life: T,
              size: r * 0.35,
              tint: 0xfff2d0,
              add: true,
              hold: true,
              emit: this.emitter(40, (l) => this.spawn({ tex: 'dot', x: l.p.x + rnd(-1.5, 1.5), y: l.p.y, vx: rnd(-10, 10), vy: rnd(10, 30), life: rnd(0.2, 0.35), size: r * 0.22, size1: 0, tint: 0xffc070, add: true })),
              onEnd: () => burst(tx, ty, sh.c1, sh.c2),
            });
          }),
        );
        break;
      }
      case 'kill:avalanche': {
        // Une vague de neige déferle en anneau depuis la victime : front de poudre, mottes qui roulent, d'autres projetées
        // qui retombent et rebondissent, une seconde vague plus faible, puis une neige fine qui se dépose.
        // Tout est mat et blanc (le gel, lui, est fait de cristaux bleus).
        this.spawn({ tex: 'dot', x, y, life: 0.2, size: r * 1.8, size1: r * 0.6, tint: 0xffffff, alpha: 0.8, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.5, size: r * 0.6, size1: r * 3.6, tint: 0xf2f8ff, alpha: 0.7, pop: true });
        const m = n(14);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU + rnd(-0.15, 0.15);
          const v = rnd(150, 200);
          this.spawn({ tex: 'smoke', x: x + Math.cos(a) * r * 0.5, y: y + Math.sin(a) * r * 0.5, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.93, life: rnd(0.8, 1.1), size: r * 0.7, size1: r * 1.9, tint: pick(SNOW), alpha: 0.7 });
        }
        for (let i = 0; i < n(22); i++) {
          const a = rnd(0, TAU);
          const v = rnd(130, 240);
          this.spawn({ tex: 'chunk', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), spin: rnd(-8, 8), drag: 0.92, life: rnd(0.7, 1.1), size: r * rnd(0.28, 0.5), size1: r * 0.15, tint: pick(SNOW), hold: true });
        }
        for (let i = 0; i < n(8); i++) {
          this.spawn({ tex: 'chunk', x: x + rnd(-0.4, 0.4) * r, y, vx: rnd(-120, 120), vy: -rnd(150, 260), grav: 600, floor: y + r * rnd(0.4, 1.4), bounce: 0.25, rot: rnd(0, TAU), spin: rnd(-10, 10), drag: 0.97, life: rnd(1, 1.3), size: r * rnd(0.3, 0.45), tint: pick(SNOW), hold: true });
        }
        this.later(0.18, () => {
          const m2 = n(8);
          for (let i = 0; i < m2; i++) {
            const a = (i / m2) * TAU + rnd(-0.3, 0.3);
            const v = rnd(90, 120);
            this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.94, life: 0.9, size: r * 0.5, size1: r * 1.4, tint: 0xf4f9ff, alpha: 0.45 });
          }
        });
        this.later(0.3, () => {
          for (let i = 0; i < n(12); i++) this.spawn({ tex: 'flake', x: x + rnd(-2.5, 2.5) * r, y: y + rnd(-2.5, 2) * r, vy: rnd(10, 25), sway: { a: 12, w: rnd(2, 4) }, rot: rnd(0, TAU), spin: rnd(-1.5, 1.5), life: rnd(0.9, 1.4), size: r * rnd(0.18, 0.3), tint: pick(SNOW), fadeIn: true });
        });
        break;
      }
    }
  }

  /** Effet de mort du tank, aux couleurs de ses pièces. */
  death(id: string | null, x: number, y: number, r: number, colors: number[]): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    const col = () => colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff;
    switch (id) {
      case 'death:eclats':
        for (let i = 0; i < n(28); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(60, 180);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.7, 1.2), size: r * rnd(0.3, 0.6), tint: col(), spin: rnd(-12, 12), drag: 0.9 });
        }
        this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 1.8, size1: r * 0.6, tint: 0xffc070, add: true });
        break;
      case 'death:fumee':
        for (let i = 0; i < n(14); i++) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'smoke', x: x + Math.cos(a) * r * 0.5, y: y + Math.sin(a) * r * 0.5, vx: Math.cos(a) * 18, vy: Math.sin(a) * 18 - 14, life: rnd(1, 1.6), size: r * 0.9, size1: r * 2, tint: 0x2b2e33, alpha: 0.45, drag: 0.97 });
        }
        break;
      case 'death:implosion':
        for (let i = 0; i < n(36); i++) {
          const a = rnd(0, Math.PI * 2);
          const d = r * rnd(1.6, 2.2);
          this.spawn({ tex: 'dot', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, vx: -Math.cos(a) * d * 2.2, vy: -Math.sin(a) * d * 2.2, life: 0.45, size: r * 0.4, size1: r * 0.1, tint: col(), add: true });
        }
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r * 3, size1: r * 0.3, tint: 0xc88cff, alpha: 0.8, add: true });
        break;
      case 'death:vaporisation':
        for (let i = 0; i < n(40); i++) {
          this.spawn({ tex: 'dot', x: x + rnd(-r, r), y: y + rnd(-r, r), vx: rnd(-20, 20), vy: rnd(-90, -30), life: rnd(0.8, 1.5), size: r * rnd(0.3, 0.6), size1: 0, tint: col(), add: true, fadeIn: true });
        }
        break;
      case 'death:brisglace':
        // Le tank vole en éclats de glace (cristaux bleutés mêlés aux couleurs des pièces), reflets.
        this.spawn({ tex: 'dot', x, y, life: 0.25, size: r * 1.8, size1: r * 0.5, tint: 0xd8f3ff, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.35, size: r * 0.6, size1: r * 2.6, tint: 0xffffff, alpha: 0.7, add: true, pop: true });
        for (let i = 0; i < n(26); i++) {
          const a = rnd(0, TAU);
          const v = rnd(70, 200);
          this.spawn({ tex: i % 2 ? 'crystal' : 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, spin: rnd(-10, 10), drag: 0.9, life: rnd(0.7, 1.2), size: r * rnd(0.28, 0.55), tint: Math.random() < 0.6 ? pick(ICE) : col(), hold: true });
        }
        for (let i = 0; i < n(10); i++) this.spawn({ tex: 'star', x: x + rnd(-1.5, 1.5) * r, y: y + rnd(-1.5, 1.5) * r, life: rnd(0.3, 0.6), size: r * 0.35, size1: 0, tint: 0xffffff, add: true, fadeIn: true });
        break;
      case 'death:fusion':
        // Le tank (à ses couleurs) se ratatine et fond en une flaque incandescente qui refroidit.
        this.spawn({ tex: 'orb', x, y, life: 0.6, size: r * 1.6, size1: r * 0.5, tint: col(), tint2: 0xff7a1a });
        this.spawn({ tex: 'dot', x, y: y + r * 0.2, life: 1.6, size: r * 2.2, size1: r * 2.6, tint: 0xff9a2a, tint2: 0x801500, alpha: 0.7, add: true, below: true });
        const rot = rnd(0, TAU);
        this.spawn({ tex: 'splat', x, y: y + r * 0.2, rot, life: 2.1, size: r * 0.5, size1: r * 1.9, tint: 0xffb040, tint2: 0x3a0c04, pop: true, hold: true, below: true });
        this.spawn({ tex: 'splat', x, y: y + r * 0.2, rot, life: 1.5, size: r * 0.3, size1: r * 1.2, tint: 0xfff0a0, tint2: 0xff5a10, alpha: 0.9, add: true, pop: true, below: true });
        for (let i = 0; i < n(14); i++) {
          this.spawn({ tex: 'orb', x: x + rnd(-0.8, 0.8) * r, y: y + rnd(-0.8, 0.8) * r, vx: rnd(-40, 40), vy: rnd(-30, 50), grav: 80, drag: 0.93, life: rnd(0.6, 1), size: r * rnd(0.1, 0.18), size1: r * 0.04, tint: col(), tint2: 0xff5a1a });
        }
        for (let i = 0; i < n(5); i++) this.spawn({ tex: 'smoke', x: x + rnd(-0.6, 0.6) * r, y, vy: -rnd(20, 40), life: rnd(1.2, 1.6), size: r * 0.6, size1: r * 1.6, tint: 0x55504c, alpha: 0.35, drag: 0.98 });
        for (let i = 0; i < n(8); i++) this.spawn({ tex: 'dot', x: x + rnd(-0.8, 0.8) * r, y: y + rnd(-0.3, 0.5) * r, vy: -rnd(30, 60), life: rnd(0.6, 1), size: r * 0.25, size1: r * 0.05, tint: 0xffc070, tint2: 0xff3a12, add: true });
        break;
      case 'death:deconnexion': {
        // Le tank se défait en pixels qui clignotent et glissent, lignes de balayage, puis extinction façon écran.
        const step = r * 0.36;
        for (let gx = -r; gx <= r; gx += step)
          for (let gy = -r; gy <= r; gy += step) {
            if (gx * gx + gy * gy > r * r || Math.random() > this.budget) continue;
            this.spawn({ tex: 'pixel', x: x + gx, y: y + gy, vx: Math.random() < 0.3 ? rnd(-80, 80) : 0, drag: 0.85, life: rnd(0.25, 0.9), hold: true, size: step * 0.92 * PIX, tint: Math.random() < 0.5 ? col() : pick([0x3dfcff, 0xff3df0, 0xffffff]), blink: rnd(12, 24) });
          }
        for (let j = 0; j < 4; j++)
          this.later(j * 0.08, () => {
            const yy = y + rnd(-1, 1) * r;
            const xx = x + rnd(-0.3, 0.3) * r;
            const L = r * rnd(0.8, 1.5);
            this.line(xx - L, yy, xx + L, yy, { life: 0.18, size: r * 0.12, tint: pick([0x3dfcff, 0xff3df0]), blink: 30 });
          });
        this.later(0.55, () => {
          this.line(x - r * 1.6, y, x + r * 1.6, y, { life: 0.22, size: r * 0.35, size1: r * 0.02, tint: 0xffffff });
          this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 0.5, size1: 0, tint: 0xffffff, add: true });
        });
        break;
      }
      case 'death:racines': {
        // Des racines brunes poussent en se tordant depuis l'épave, des feuilles y bourgeonnent.
        this.spawn({ tex: 'smoke', x, y, life: 0.8, size: r * 0.8, size1: r * 1.8, tint: 0x5a4632, alpha: 0.45 });
        for (let i = 0; i < n(4); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * 60, vy: Math.sin(a) * 60, drag: 0.92, spin: rnd(-6, 6), life: 0.8, size: r * 0.3, tint: col() });
        }
        const m = n(4);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU + rnd(-0.4, 0.4);
          const sp = r * rnd(2.6, 3.4);
          const curl = rnd(1.5, 3) * sign();
          const tint = pick([0x6b4a2b, 0x5a3d22, 0x7a5532]);
          // La tête de la racine dépose des segments orientés le long de son trajet, de plus en plus fins.
          const grow = this.emitter(11, (l) => {
            const ang = Math.atan2(l.vy, l.vx);
            const prog = 1 - l.life / l.max;
            this.spawn({ tex: 'orb', x: l.p.x, y: l.p.y, rot: ang, len: r * 0.34 * ORB, size: r * (0.17 - prog * 0.11) * ORB, life: 1.5 - prog * 0.4, tint, hold: true, below: true });
            if (Math.random() < 0.22) this.spawn({ tex: 'leaf', x: l.p.x, y: l.p.y, rot: ang + sign() * 1.1, life: 1.3, size: r * 0.05, size1: r * 0.35, tint: pick([0x4f8a2a, 0x6aa83a]), pop: true, hold: true, below: true });
          });
          this.spawn({
            tex: 'orb',
            x: x + Math.cos(a) * r * 0.3,
            y: y + Math.sin(a) * r * 0.3,
            vx: Math.cos(a) * sp,
            vy: Math.sin(a) * sp,
            drag: 0.985,
            life: rnd(0.6, 0.8),
            size: r * 0.2,
            size1: r * 0.08,
            tint,
            emit: (l, dt) => {
              // Courbure : la vitesse tourne peu à peu.
              const ang = Math.atan2(l.vy, l.vx) + curl * dt;
              const v = Math.hypot(l.vx, l.vy);
              l.vx = Math.cos(ang) * v;
              l.vy = Math.sin(ang) * v;
              grow(l, dt);
            },
          });
        }
        break;
      }
      case 'death:naufrage':
        // Flaque d'eau, deux rides, bulles qui remontent ; quelques débris aux couleurs des pièces coulent.
        this.spawn({ tex: 'smoke', x, y, life: 1.8, size: r * 1, size1: r * 2.4, tint: 0x1f6fa8, alpha: 0.6, pop: true, hold: true, below: true });
        for (let j = 0; j < 2; j++) this.later(j * 0.35, () => this.spawn({ tex: 'ring', x, y, life: 0.7, size: r * 0.6, size1: r * 2.8, tint: 0x8fd8ff, alpha: 0.32, pop: true, below: true }));
        for (let i = 0; i < n(18); i++) {
          const bx = x + rnd(-0.8, 0.8) * r;
          const by = y + rnd(-0.4, 0.6) * r;
          this.later(rnd(0, 0.6), () => this.spawn({ tex: 'bubble', x: bx, y: by, vy: -rnd(30, 70), sway: { a: 15, w: rnd(4, 7) }, life: rnd(0.8, 1.3), size: r * rnd(0.12, 0.22), size1: r * 0.3, tint: 0xd4f4ff, alpha: 0.9, fadeIn: true }));
        }
        for (let i = 0; i < n(6); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * 30, vy: Math.sin(a) * 30, drag: 0.95, spin: rnd(-2, 2), rot: a, life: 1, size: r * 0.4, size1: r * 0.15, tint: col() });
        }
        break;
      case 'death:nebuleuse':
        // Nuage de nébuleuse rose, violet et cyan qui s'étend, étoiles qui scintillent dedans.
        this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 1.8, size1: r * 0.5, tint: 0xffd6ff, add: true });
        for (let i = 0; i < n(12); i++) {
          this.spawn({ tex: 'smoke', x: x + rnd(-0.6, 0.6) * r, y: y + rnd(-0.6, 0.6) * r, vx: rnd(-18, 18), vy: rnd(-18, 18), spin: rnd(-0.5, 0.5), life: rnd(1.6, 2.2), size: r * 0.8, size1: r * rnd(1.8, 2.6), tint: pick([0xff4fd8, 0x8a5cff, 0x3dc9ff, 0xff8a5c, 0x6a3cff]), alpha: 0.42, add: true, fadeIn: true });
        }
        for (let i = 0; i < n(18); i++) {
          const a = rnd(0, TAU);
          const d = r * Math.sqrt(Math.random()) * 1.8;
          this.spawn({ tex: 'star', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, life: rnd(1, 1.8), size: r * rnd(0.15, 0.3), tint: pick([0xffffff, 0xfff2c0, 0xcfe0ff]), add: true, fadeIn: true, pulse: rnd(6, 10) });
        }
        break;
      case 'death:statue':
        // Éclats de marbre blanc qui retombent et rebondissent, poussière claire.
        this.spawn({ tex: 'dot', x, y, life: 0.2, size: r * 1.4, size1: r * 0.4, tint: 0xffffff, add: true });
        for (let i = 0; i < n(24); i++) {
          const a = rnd(0, TAU);
          const v = rnd(60, 170);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 120, rot: rnd(0, TAU), grav: 500, floor: y + r * rnd(0.3, 1.1), bounce: 0.3, spin: rnd(-9, 9), drag: 0.97, life: rnd(1, 1.5), hold: true, size: r * rnd(0.25, 0.5), tint: pick([0xf4f1ea, 0xe3ddd2, 0xcfc8bb, 0xb8b0a2]) });
        }
        for (let i = 0; i < n(8); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'smoke', x: x + Math.cos(a) * r * 0.4, y: y + Math.sin(a) * r * 0.4, vx: Math.cos(a) * 20, vy: Math.sin(a) * 20 - 8, drag: 0.97, life: rnd(0.9, 1.3), size: r * 0.5, size1: r * 1.6, tint: 0xe8e2d6, alpha: 0.32 });
        }
        break;
      case 'death:ferraille':
        // Écrous, boulons et plaques aux couleurs des pièces qui retombent, fumée noire, étincelles.
        for (let i = 0; i < n(10); i++) {
          const a = rnd(0, TAU);
          const v = rnd(90, 200);
          this.spawn({ tex: i % 2 ? 'nut' : 'screw', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 100, rot: rnd(0, TAU), grav: 450, floor: y + r * rnd(0.3, 1.1), bounce: 0.4, spin: rnd(-12, 12), drag: 0.97, life: rnd(1.1, 1.5), hold: true, size: r * rnd(0.28, 0.38), tint: pick([0xb4bcc4, 0x8a939c, 0xc89b52]) });
        }
        for (let i = 0; i < n(10); i++) {
          const a = rnd(0, TAU);
          const v = rnd(70, 170);
          const s = r * rnd(0.28, 0.42);
          this.spawn({ tex: 'pixel', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 110, rot: rnd(0, TAU), grav: 450, floor: y + r * rnd(0.3, 1.1), bounce: 0.3, spin: rnd(-8, 8), drag: 0.97, life: rnd(1.2, 1.6), hold: true, size: s * PIX, len: s * rnd(1.2, 1.6) * PIX, tint: col() });
        }
        for (let i = 0; i < n(7); i++) this.spawn({ tex: 'smoke', x: x + rnd(-0.5, 0.5) * r, y: y + rnd(-0.5, 0.5) * r, vy: -rnd(15, 35), life: rnd(1.2, 1.8), size: r * 0.5, size1: r * 1.8, tint: 0x3a3a3e, alpha: 0.5, drag: 0.98 });
        for (let i = 0; i < n(5); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * 170, vy: Math.sin(a) * 170, rot: a, life: 0.3, size: r * 0.6, size1: 0, tint: 0xffa040, add: true, drag: 0.9 });
        }
        break;
      case 'death:fantome': {
        // Un fantôme pâle s'élève en ondulant, dans un halo, et laisse un voile derrière lui.
        const o = { x, y, vy: -r * 1.5, sway: { a: r * 1.2, w: 3.5 }, ph: 0, life: 1.8, fadeIn: true };
        this.spawn({ tex: 'dot', ...o, size: r * 2, size1: r * 2.2, tint: 0xbfd4ff, alpha: 0.4, add: true });
        this.spawn({ tex: 'ghost', ...o, size: r * 1.1, size1: r * 1.4, tint: 0xeef4ff, alpha: 0.85, emit: this.emitter(9, (l) => this.spawn({ tex: 'smoke', x: l.p.x + rnd(-4, 4), y: l.p.y + r * 0.4, vy: -10, life: 0.8, size: r * 0.6, size1: r * 1.0, tint: 0xcfdcff, alpha: 0.25, add: true })) });
        for (let i = 0; i < n(10); i++) this.spawn({ tex: 'dot', x: x + rnd(-1, 1) * r, y: y + rnd(-0.5, 0.8) * r, vy: -rnd(20, 50), life: rnd(0.8, 1.3), size: r * 0.2, size1: 0, tint: 0xe6eeff, add: true, fadeIn: true });
        break;
      }
      case 'death:encre': {
        // Grande tache d'encre au sol (liseré violet), gouttes projetées et éclaboussures autour.
        const rot = rnd(0, TAU);
        this.spawn({ tex: 'splat', x, y, rot, life: 1.6, size: r * 0.6, size1: r * 2.35, tint: 0x4a2a78, pop: true, hold: true, below: true });
        this.spawn({ tex: 'splat', x, y, rot, life: 1.6, size: r * 0.5, size1: r * 2.1, tint: 0x120a1c, alpha: 0.95, pop: true, hold: true, below: true });
        this.spawn({ tex: 'dot', x, y, life: 0.5, size: r * 2.4, size1: r * 1.6, tint: 0x6a3cff, alpha: 0.35, add: true });
        for (let i = 0; i < n(5); i++) {
          const a = rnd(0, TAU);
          const d = r * rnd(1.2, 2);
          this.later(0.06 + i * 0.03, () => this.spawn({ tex: 'splat', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, rot: rnd(0, TAU), life: 1.3, size: r * 0.1, size1: r * rnd(0.35, 0.6), tint: pick([0x1d1030, 0x2d1747]), pop: true, hold: true, below: true }));
        }
        for (let i = 0; i < n(14); i++) {
          const a = rnd(0, TAU);
          const v = rnd(100, 220);
          this.spawn({ tex: 'orb', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.86, life: rnd(0.6, 1), hold: true, size: r * rnd(0.15, 0.28), tint: pick(INK) });
        }
        break;
      }
      case 'death:sablier': {
        // Un sablier se dessine ; le tank s'effrite rangée par rangée en sable qui s'écoule par le goulot et s'amasse.
        const w = r * 1.1;
        const top = y - r * 1.25;
        const bot = y + r * 1.4;
        const frame = { life: 1.6, size: r * 0.12, tint: 0xfff2d6, alpha: 0.5, fadeIn: true };
        this.line(x - w, top, x + w, bot, frame);
        this.line(x + w, top, x - w, bot, frame);
        this.line(x - w * 1.15, top, x + w * 1.15, top, { ...frame, size: r * 0.3, tint: 0xd9a54a, alpha: 0.9 });
        this.line(x - w * 1.15, bot, x + w * 1.15, bot, { ...frame, size: r * 0.3, tint: 0xd9a54a, alpha: 0.9 });
        const per = n(9);
        for (let b = 0; b < 4; b++)
          this.later(b * 0.14, () => {
            for (let i = 0; i < per; i++) {
              const py = y - r + (b + Math.random()) * (r / 2);
              const half = Math.sqrt(Math.max(0, r * r - (py - y) ** 2));
              const px = x + rnd(-1, 1) * half * 0.9;
              this.spawn({ tex: 'orb', x: px, y: py, vx: (x - px) * 1.8, vy: rnd(0, 20), grav: 340, drag: 0.97, floor: bot - r * rnd(0.08, 0.4) * (1 - Math.abs(px - x) / (r * 1.5)), bounce: 0, life: rnd(1.3, 1.7), hold: true, size: r * rnd(0.13, 0.2), tint: Math.random() < 0.4 ? col() : pick(SAND) });
            }
          });
        for (let i = 0; i < n(4); i++) this.later(0.4 + i * 0.1, () => this.spawn({ tex: 'smoke', x: x + rnd(-0.5, 0.5) * r, y: bot - r * 0.2, vx: rnd(-20, 20), life: 0.9, size: r * 0.5, size1: r * 1.2, tint: 0xd9b77a, alpha: 0.3 }));
        break;
      }
      case 'death:ascension': {
        // Pilier de lumière qui monte vers le ciel, grains dorés qui s'y élèvent, plumes qui retombent.
        const H = r * 7;
        this.line(x, y + r * 0.3, x, y - H, { life: 0.9, size: r * 2.2, size1: r * 0.4, tint: 0xfff4d0, alpha: 0.85, fadeIn: true });
        this.line(x, y + r * 0.3, x, y - H, { life: 0.8, size: r * 0.8, size1: r * 0.1, tint: 0xffffff, fadeIn: true });
        this.spawn({ tex: 'ring', x, y, life: 0.6, size: r * 0.5, size1: r * 2.8, tint: 0xffd76a, add: true, pop: true });
        this.spawn({ tex: 'dot', x, y, life: 0.35, size: r * 2, size1: r * 0.6, tint: 0xfff2c0, add: true });
        for (let i = 0; i < n(14); i++) this.spawn({ tex: 'dot', x: x + rnd(-0.6, 0.6) * r, y: y - rnd(0, 1) * r, vy: -rnd(r * 3, r * 5), life: rnd(0.6, 1), size: r * 0.25, size1: 0, tint: 0xffe39a, add: true, fadeIn: true });
        for (let i = 0; i < n(12); i++) this.spawn({ tex: 'feather', x: x + rnd(-0.8, 0.8) * r, y: y - rnd(0, 0.5) * H, vx: rnd(-30, 30), vy: rnd(10, 30), sway: { a: 30, w: rnd(2, 3) }, rot: rnd(0, TAU), spin: rnd(-2, 2), life: rnd(1.4, 2), size: r * rnd(0.4, 0.55), tint: pick([0xffffff, 0xfff6e2]), hold: true, fadeIn: true });
        break;
      }
      case 'death:bouledeneige': {
        // L'épave s'enroule dans une boule de neige (débris aux couleurs des pièces aspirés, flocons en spirale) qui
        // gonfle, puis éclate en mottes, flocons et poudre. La boule est au sol (sous les balles).
        const c = { x, y };
        this.spawn({ tex: 'snowball', x, y, life: 0.5, size: r * 0.5, size1: r * 1.5, tint: 0xffffff, spin: rnd(-2, 2), pop: true, hold: true, below: true });
        for (let i = 0; i < n(8); i++) {
          const a = rnd(0, TAU);
          const d = r * rnd(1.3, 1.9);
          this.spawn({ tex: 'shard', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, vx: -Math.cos(a) * d * 2.4, vy: -Math.sin(a) * d * 2.4, rot: rnd(0, TAU), spin: rnd(-10, 10), life: 0.4, size: r * 0.32, size1: r * 0.1, tint: col() });
        }
        const w = rnd(4.5, 5.5) * sign();
        for (let i = 0; i < n(10); i++) {
          this.spawn({ tex: 'flake', x, y, life: 0.45, size: r * rnd(0.2, 0.3), tint: pick(SNOW), spin: rnd(-3, 3), fadeIn: true, orbit: { c, a: rnd(0, TAU), r: r * rnd(1.4, 2), w, dr: -r * 2.5 } });
        }
        this.later(0.42, () => {
          this.spawn({ tex: 'dot', x, y, life: 0.22, size: r * 2, size1: r * 0.6, tint: 0xffffff, add: true });
          this.spawn({ tex: 'ring', x, y, life: 0.4, size: r * 0.6, size1: r * 3, tint: 0xf2f8ff, alpha: 0.8, pop: true });
          for (let i = 0; i < n(20); i++) {
            const a = rnd(0, TAU);
            const v = rnd(90, 220);
            this.spawn({ tex: 'chunk', x: x + Math.cos(a) * r * 0.4, y: y + Math.sin(a) * r * 0.4, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), spin: rnd(-9, 9), drag: 0.9, life: rnd(0.7, 1.1), size: r * rnd(0.28, 0.5), size1: r * 0.12, tint: Math.random() < 0.15 ? col() : pick(SNOW), hold: true });
          }
          for (let i = 0; i < n(12); i++) {
            const a = rnd(0, TAU);
            const v = rnd(40, 100);
            this.spawn({ tex: 'flake', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.95, sway: { a: 12, w: rnd(2, 4) }, rot: rnd(0, TAU), spin: rnd(-2, 2), life: rnd(1, 1.5), size: r * rnd(0.2, 0.32), tint: pick(SNOW), hold: true });
          }
          for (let i = 0; i < n(6); i++) {
            const a = rnd(0, TAU);
            this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * 40, vy: Math.sin(a) * 40, drag: 0.95, life: rnd(0.8, 1.1), size: r * 0.6, size1: r * 1.8, tint: 0xf4f9ff, alpha: 0.5 });
          }
        });
        break;
      }
      case 'death:bonhomme': {
        // Un petit bonhomme de neige (trois boules, nez carotte, chapeau, écharpe) surgit à la place du tank dans une
        // bouffée de neige, se dandine, puis fond : il s'affaisse et s'étale en flaque en fumant, avant une dernière
        // bouffée. Posé au sol : sous les balles et les tanks.
        const S = r * 1.7;
        const s0 = scaleOf(S);
        const base = y + 22 * s0; // pieds du bonhomme (22 px sous le centre de la case)
        const LIFE = 2.4;
        const MELT = 1.3;
        const melt = (age: number) => Math.min(1, Math.max(0, (age - MELT) / (LIFE - MELT)));
        // Flaque sous le bonhomme (créée d'abord pour être dessous), invisible tant qu'il ne fond pas.
        this.spawn({
          tex: 'splat',
          x,
          y: base - r * 0.05,
          rot: rnd(0, TAU),
          life: LIFE + 0.6,
          size: r * 0.1,
          tint: 0xcfe6f7,
          below: true,
          emit: (l) => {
            const m = melt(l.max - l.life);
            const s = scaleOf(r * (0.3 + 1.2 * m));
            l.p.scaleX = s;
            l.p.scaleY = s * 0.55;
            l.p.alpha = 0.55 * Math.min(1, m * 4) * Math.min(1, l.life / 0.5);
          },
        });
        const drip = this.emitter(10, (l) => {
          const h = 44 * l.p.scaleY;
          const px = x + rnd(-10, 10) * l.p.scaleX;
          if (Math.random() < 0.5) this.spawn({ tex: 'smoke', x: px, y: base - h * rnd(0.2, 0.8), vy: -rnd(15, 30), life: rnd(0.5, 0.8), size: r * 0.3, size1: r * 0.8, tint: 0xf2f8ff, alpha: 0.35 });
          else this.spawn({ tex: 'orb', x: px, y: base - h * rnd(0.1, 0.5), vy: rnd(25, 45), floor: base, bounce: 0, life: rnd(0.35, 0.5), size: r * rnd(0.08, 0.12), tint: 0xbfe0f7, alpha: 0.9, below: true });
        });
        this.spawn({
          tex: 'snowman',
          x,
          y,
          life: LIFE,
          size: S * 0.05,
          tint: 0xffffff,
          below: true,
          emit: (l, dt) => {
            const age = l.max - l.life;
            const m = melt(age);
            let sx = s0;
            let sy = s0;
            if (age < 0.25) sx = sy = s0 * backOut(age / 0.25);
            else if (m > 0) {
              sy = s0 * (1 - 0.8 * m);
              sx = s0 * (1 + 0.3 * m);
              drip(l, dt);
            }
            l.p.scaleX = sx;
            l.p.scaleY = sy;
            l.p.x = x;
            l.p.y = base - 22 * sy; // les pieds restent au sol
            l.p.rotation = age > 0.25 && m === 0 ? Math.sin((age - 0.25) * 6) * 0.09 : 0;
            l.p.alpha = 1 - m * m;
          },
        });
        this.spawn({ tex: 'dot', x, y, life: 0.25, size: r * 1.8, size1: r * 0.5, tint: 0xffffff, alpha: 0.8, add: true });
        for (let i = 0; i < n(8); i++) {
          const a = rnd(0, TAU);
          this.spawn({ tex: 'smoke', x: x + Math.cos(a) * r * 0.4, y: base - r * 0.3, vx: Math.cos(a) * 45, vy: Math.sin(a) * 20 - 10, drag: 0.94, life: rnd(0.6, 0.9), size: r * 0.5, size1: r * 1.3, tint: 0xf4f9ff, alpha: 0.6 });
        }
        for (let i = 0; i < n(10); i++) {
          const a = rnd(0, TAU);
          const v = rnd(60, 130);
          this.spawn({ tex: 'flake', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, drag: 0.93, rot: rnd(0, TAU), spin: rnd(-3, 3), life: rnd(0.7, 1), size: r * rnd(0.18, 0.28), tint: pick(SNOW), hold: true });
        }
        for (let i = 0; i < n(5); i++) {
          const a = rnd(0, TAU);
          const v = rnd(100, 180);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: rnd(0, TAU), spin: rnd(-10, 10), drag: 0.9, life: rnd(0.5, 0.8), size: r * rnd(0.25, 0.4), tint: col() });
        }
        this.later(LIFE - 0.15, () => {
          for (let i = 0; i < n(5); i++) this.spawn({ tex: 'smoke', x: x + rnd(-0.5, 0.5) * r, y: base - r * 0.2, vy: -rnd(15, 30), life: rnd(0.7, 1), size: r * 0.4, size1: r * 1.1, tint: 0xf4f9ff, alpha: 0.45 });
          for (let i = 0; i < n(6); i++) this.spawn({ tex: 'flake', x: x + rnd(-0.7, 0.7) * r, y: base - r * rnd(0, 0.5), vx: rnd(-20, 20), vy: -rnd(20, 40), rot: rnd(0, TAU), spin: rnd(-2, 2), life: rnd(0.7, 1), size: r * rnd(0.16, 0.24), tint: pick(SNOW), fadeIn: true });
        });
        break;
      }
    }
  }

  // ─── Animation ────────────────────────────────────────────────────────

  update(dt: number): void {
    this.time += dt;
    if (this.timers.length) {
      const ready: (() => void)[] = [];
      for (let i = this.timers.length - 1; i >= 0; i--) {
        const tm = this.timers[i];
        tm.t -= dt;
        if (tm.t > 0) continue;
        this.timers[i] = this.timers[this.timers.length - 1];
        this.timers.pop();
        ready.push(tm.fn);
      }
      if (this.budget > 0) for (const f of ready) f();
    }
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i];
      l.life -= dt;
      if (l.life <= 0) {
        l.layer.removeParticle(l.p);
        this.live[i] = this.live[this.live.length - 1];
        this.live.pop();
        l.onEnd?.(l);
        continue;
      }
      const forever = l.max > 1e8;
      const k = forever ? 1 : l.life / l.max; // 1 → 0
      const age = l.max - l.life;
      const p = l.p;
      const sw = l.sway ? Math.cos(l.ph + age * l.sway.w) * l.sway.a * dt : 0;
      if (l.grav) l.vy += l.grav * dt;
      if (l.drag !== 1) {
        l.vx *= l.drag ** (dt * 60);
        l.vy *= l.drag ** (dt * 60);
      }
      if (l.orbit) {
        const o = l.orbit;
        o.a += o.w * dt;
        if (o.dr) o.r += o.dr * dt;
        l.ox += l.vx * dt + sw;
        l.oy += l.vy * dt;
        p.x = o.c.x + Math.cos(o.a) * o.r + l.ox;
        p.y = o.c.y + Math.sin(o.a) * o.r * (o.sq ?? 1) + l.oy;
        if (l.face !== undefined) {
          const d = o.w < 0 ? -1 : 1;
          p.rotation = Math.atan2(Math.cos(o.a) * (o.sq ?? 1) * d, -Math.sin(o.a) * d) + l.face;
        }
      } else {
        p.x += l.vx * dt + sw;
        p.y += l.vy * dt;
        if (l.floor !== undefined && p.y > l.floor && l.vy > 0) {
          p.y = l.floor;
          l.vy *= -l.bounce;
          l.vx *= 0.6;
          l.spin *= 0.5;
        }
        if (l.face !== undefined) p.rotation = Math.atan2(l.vy, l.vx) + l.face;
      }
      if (l.face === undefined) p.rotation += l.spin * dt;
      const u = l.pop ? 1 - k ** 4 : 1 - k;
      let s = l.s0 + (l.s1 - l.s0) * u;
      if (l.pulse) s *= 1 + 0.16 * Math.sin(l.ph + age * l.pulse);
      let sx = l.lx ?? s;
      if (l.flap) sx *= 0.25 + 0.75 * Math.abs(Math.sin(l.ph + age * l.flap));
      p.scaleX = sx;
      p.scaleY = s;
      if (!forever) {
        const fin = l.fadeIn ? Math.min(1, (1 - k) * (l.hold ? 8 : 4)) : 1;
        const fout = l.hold ? Math.min(1, k * 4) : l.fadeIn ? Math.min(1, k * 2) : k;
        const bl = l.blink && Math.sin(l.ph + age * l.blink) < -0.3 ? 0.15 : 1;
        p.alpha = l.a0 * fin * fout * bl;
      }
      if (l.tint2 !== undefined) p.tint = lerpColor(l.tint2, l.tint, k);
      l.emit?.(l, dt);
    }
    for (let i = this.boltList.length - 1; i >= 0; i--) {
      const b = this.boltList[i];
      b.life -= dt;
      b.g.alpha = Math.max(0, b.life / b.max);
      if (b.life <= 0) {
        b.g.destroy();
        this.boltList.splice(i, 1);
      }
    }
  }

  get count(): number {
    return this.live.length;
  }
}
